import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { flushSync, createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { needsTokenRefresh, refreshToken, isTokenValid } from './auth';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { useMusic } from './hooks/useMusic';
import { playSoundEffect } from './hooks/useSoundEffects';
import { getWebSocketAuthMessage, API_URL, WS_URL } from './config/apiConfig';
import CustomDropdown from './CustomDropdown';
import { updateDiscordPresence, clearDiscordPresence } from './hooks/useDiscordPresence';
import { useContainerScale } from './hooks/useViewportScale';
import OrderDesign from './OrderDesign';
import { OrderCardBubbles } from './components/OrderCardBubbles';
import { getAvatarImage } from './avatars/avatarManager';
import { TutorialMessageBox } from './tutorial/index';
import { useNotifications } from './notifications';
import './DispatchAsset.css';

// Faction colors for orb backgrounds
const factionColors = {
  'Remnants of Humanity': '#556B2F',
  'Exolot Dominion': '#4B0082',
  'League of Shadows': '#1A1A1A',
  'Ra\'s Children': '#F4A460',
  'Asrah': '#8B0000',
  'The Sentients': '#ADD8E6',
  'Combined': '#FFA500'
};

// Faction to hero ID mapping
const factionHeroIds = {
  'Asrah': 377,
  'The Hollow': 394,
  'The Forsaken Pact': 393,
  'Exolot Dominion': 381,
  'The Corrupted Core': 390,
  'The Iron Alliance': 395,
  'League of Shadows': 383,
  'The Conspiracy': 389,
  'The Shadow Consortium': 402,
  'Ra\'s Children': 384,
  'The Divine Syndicate': 392,
  'The Covenant': 391,
  'Remnants of Humanity': 386,
  'The Last Crusade': 396,
  'The Silent Accord': 403
};

// Resource type -> faction color (for orb behind resource icon; uses primary single-resource faction)
const resourceOrbColors = {
  lavender: '#4B0082',   // Exolot Dominion
  biomass: '#8B0000',    // Asrah
  foodRations: '#556B2F', // Remnants of Humanity
  stone: '#F4A460',      // Ra's Children
  erray: '#1A1A1A'       // League of Shadows
};

// Faction resource costs (per battle: 80 per resource)
const factionResourceCosts = {
  'Asrah': ['biomass'],
  'The Hollow': ['biomass', 'stone'],
  'The Forsaken Pact': ['biomass', 'foodRations'],
  'Exolot Dominion': ['lavender'],
  'The Corrupted Core': ['lavender', 'biomass'],
  'The Iron Alliance': ['lavender', 'foodRations'],
  'League of Shadows': ['erray'],
  'The Conspiracy': ['biomass', 'erray'],
  'The Shadow Consortium': ['lavender', 'erray'],
  'Ra\'s Children': ['stone'],
  'The Divine Syndicate': ['lavender', 'stone'],
  'The Covenant': ['erray', 'stone'],
  'Remnants of Humanity': ['foodRations'],
  'The Last Crusade': ['stone', 'foodRations'],
  'The Silent Accord': ['erray', 'foodRations']
};

const RESOURCE_COST_PER_BATTLE = 80;

// Carousel image per faction (filename in public/images). Use human_carousel.jpg for factions without a specific image.
const factionCarouselImages = {
  'Remnants of Humanity': 'human_carousel.jpg',
  'Exolot Dominion': 'wengol_carousel.jpg',
  'League of Shadows': 'rift_carousel.jpg',
  'Ra\'s Children': 'ra_carousel.jpg',
  'Asrah': 'borran_carousel.jpg',
  'The Hollow': 'ptahrr_carousel.jpg',
  'The Forsaken Pact': 'essia_carousel.jpg',
  'The Corrupted Core': 'lenk_carousel.jpg',
  'The Iron Alliance': 'iron_carousel.jpg',
  'The Conspiracy': 'arran_carousel.jpg',
  'The Shadow Consortium': 'negara_carousel.jpg',
  'The Divine Syndicate': 'divine_carousel.jpg',
  'The Covenant': 'thoth_carousel.jpg',
  'The Last Crusade': 'zayd_carousel.jpg',
  'The Silent Accord': 'lana_carousel.jpg'
};

const getCarouselImage = (factionName) => {
  const filename = factionCarouselImages[factionName];
  if (!filename) throw new Error(`No carousel image defined for faction: ${factionName}`);
  return `/images/${filename}`;
};

// Hero name displayed below each faction carousel image (canonical spellings from codebase)
const FACTION_HERO_NAMES = {
  'Exolot Dominion': 'Wengol Ussek',
  'League of Shadows': 'Rift Tyrant Sul\'ma',
  'Ra\'s Children': 'Nehebkau',
  'Remnants of Humanity': 'Samuel Johnson',
  'The Conspiracy': 'Arr\'an',
  'The Corrupted Core': 'Lenk Arrek',
  'The Covenant': 'Thoth',
  'The Divine Syndicate': 'Troa Ossek',
  'The Forsaken Pact': 'Essia Luhr',
  'The Hollow': 'Ptahrr',
  'The Iron Alliance': 'Sandra Lehnsherr',
  'The Last Crusade': 'Zayd Al-Karim',
  'The Shadow Consortium': 'Negara Olanea',
  'The Silent Accord': 'Lana Monares',
  'Asrah': 'Borran'
};

const getFactionHeroName = (factionName) => FACTION_HERO_NAMES[factionName] || '';

// Get faction orb background color/gradient (matching Strategy.js and WareEffort.js implementation)
const getFactionOrbBackground = (factionName) => {
  if (!factionName || factionName === 'Unknown') {
    return '#ccc';
  }

  // Check if it's a combined faction and return gradient
  if (factionName === 'The Conspiracy') {
    return 'linear-gradient(to right, #8B0000 50%, #1A1A1A 50%)';
  } else if (factionName === 'The Corrupted Core') {
    return 'linear-gradient(to right, #4B0082 50%, #8B0000 50%)';
  } else if (factionName === 'The Covenant') {
    return 'linear-gradient(to right, #F4A460 50%, #1A1A1A 50%)';
  } else if (factionName === 'The Divine Syndicate') {
    return 'linear-gradient(to right, #F4A460 50%, #4B0082 50%)';
  } else if (factionName === 'The Forsaken Pact') {
    return 'linear-gradient(to right, #556B2F 50%, #8B0000 50%)';
  } else if (factionName === 'The Hollow') {
    return 'linear-gradient(to right, #F4A460 50%, #8B0000 50%)';
  } else if (factionName === 'The Iron Alliance') {
    return 'linear-gradient(to right, #556B2F 50%, #4B0082 50%)';
  } else if (factionName === 'The Last Crusade') {
    return 'linear-gradient(to right, #F4A460 50%, #556B2F 50%)';
  } else if (factionName === 'The Shadow Consortium') {
    return 'linear-gradient(to right, #1A1A1A 50%, #4B0082 50%)';
  } else if (factionName === 'The Silent Accord') {
    return 'linear-gradient(to right, #556B2F 50%, #1A1A1A 50%)';
  }

  // Return base faction color
  return factionColors[factionName] || '#ccc';
};

// Get faction bar color(s) for the bottom bar - returns gradient string for dual, single color for single
const getFactionBarColor = (factionName) => {
  if (!factionName || factionName === 'Unknown') {
    return '#ccc';
  }

  // Check if it's a combined faction and return gradient
  if (factionName === 'The Conspiracy') {
    return 'linear-gradient(to right, #8B0000 50%, #1A1A1A 50%)';
  } else if (factionName === 'The Corrupted Core') {
    return 'linear-gradient(to right, #4B0082 50%, #8B0000 50%)';
  } else if (factionName === 'The Covenant') {
    return 'linear-gradient(to right, #F4A460 50%, #1A1A1A 50%)';
  } else if (factionName === 'The Divine Syndicate') {
    return 'linear-gradient(to right, #F4A460 50%, #4B0082 50%)';
  } else if (factionName === 'The Forsaken Pact') {
    return 'linear-gradient(to right, #556B2F 50%, #8B0000 50%)';
  } else if (factionName === 'The Hollow') {
    return 'linear-gradient(to right, #F4A460 50%, #8B0000 50%)';
  } else if (factionName === 'The Iron Alliance') {
    return 'linear-gradient(to right, #556B2F 50%, #4B0082 50%)';
  } else if (factionName === 'The Last Crusade') {
    return 'linear-gradient(to right, #F4A460 50%, #556B2F 50%)';
  } else if (factionName === 'The Shadow Consortium') {
    return 'linear-gradient(to right, #1A1A1A 50%, #4B0082 50%)';
  } else if (factionName === 'The Silent Accord') {
    return 'linear-gradient(to right, #556B2F 50%, #1A1A1A 50%)';
  }

  // Return base faction color
  return factionColors[factionName] || '#ccc';
};

function DispatchAsset({ user, onLogout }) {
  const { t, i18n: i18nInstance } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile,
    isLargeDesktop: isContainerLargeDesktop
  } = useContainerScale();
  
  // Play battle overview music
  useMusic('/music/rock_5.mp3');

  // In-game notifications (same style as BattleOverview / other in-game warnings)
  const { showNotification, NotificationComponent } = useNotifications();
  
  // State for showing "returned from spectating" message
  const [spectateReturnMessage, setSpectateReturnMessage] = useState(null);
  const [showTutorialDispatchAsset, setShowTutorialDispatchAsset] = useState(false);
  const [seenTutorialMessages, setSeenTutorialMessages] = useState([]);

  // Fetch tutorial status
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !user) return;
    fetch(`${API_URL}/tutorial/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) setSeenTutorialMessages(data.seenMessages || []);
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user && !seenTutorialMessages.includes('dispatch_asset')) {
      const timer = setTimeout(() => setShowTutorialDispatchAsset(true), 600);
      return () => clearTimeout(timer);
    }
  }, [user, seenTutorialMessages]);
  
  // Handle return from spectating
  useEffect(() => {
    if (location.state?.spectateEnded) {
      const result = location.state.result;
      const message = result === 'win' 
        ? t('dispatchAsset.spectateEndedWin')
        : t('dispatchAsset.spectateEndedLoss');
      
      setSpectateReturnMessage(message);
      
      // Clear the location state to prevent showing message on refresh
      navigate(location.pathname, { replace: true, state: {} });
      
      // Auto-hide message after 5 seconds
      const timeout = setTimeout(() => {
        setSpectateReturnMessage(null);
      }, 5000);
      
      return () => clearTimeout(timeout);
    }
  }, [location.state, navigate, location.pathname, t]);

  // Track if we're in dev mode (to prevent fetching real rewards)
  const isDevModeRef = useRef(false);

  // ✅ DEV: Check if dev rewards are passed via state (for dev demo)
  useEffect(() => {
    if (location.state?.devRewards && Array.isArray(location.state.devRewards)) {
      console.log('🎁 DispatchAsset: Using dev rewards from state', location.state.devRewards);
      console.log('🎁 DispatchAsset: Number of dev rewards:', location.state.devRewards.length);
      isDevModeRef.current = true;
      // Create a fake game entry with dev rewards
      const fakeGameId = `dev-${Date.now()}`;
      const devRewardEntry = {
        gameId: fakeGameId,
        outcome: 'win',
        rewards: location.state.devRewards
      };
      console.log('🎁 DispatchAsset: Dev reward entry:', devRewardEntry);
      setUnviewedRewards([devRewardEntry]);
      setUnviewedRewardsCount(1);
      setActiveTab('rewards');
      // Clear the location state to prevent re-adding on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);
  
  const [strategies, setStrategies] = useState([]);
  const [allStrategies, setAllStrategies] = useState([]); // Store all strategies before filtering
  const [strategyValidations, setStrategyValidations] = useState({}); // { strategyId: { valid: boolean, insufficientOrders: [] } }
  const [factionTranslations, setFactionTranslations] = useState({}); // Map: english_name -> translated_name
  const [factions, setFactions] = useState([]); // All available factions
  // Array of faction configurations: { factionName, strategyId, count, aggressive }
  const [factionConfigs, setFactionConfigs] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0); // Which faction is shown in the carousel
  const hasRestoredCarouselRef = useRef(false);
  const [ws, setWs] = useState(null);
  const [startedGameIdState, setStartedGameIdStateRaw] = useState(null); // Store gameId when battle starts
  // ✅ DEBUG: Wrapper to log all startedGameId changes
  const setStartedGameId = (value) => {
    const stack = new Error().stack;
    const caller = stack?.split('\n')[2]?.trim() || 'unknown';
    console.log(`📊 [GameId] setStartedGameId: ${value} (was: ${startedGameIdState})`, {
      previousValue: startedGameIdState,
      newValue: value,
      startedGameIdRef: startedGameIdRef.current,
      gameProgress: gameProgressState,
      queueProgress: queueProgress,
      caller: caller.substring(0, 100)
    });
    setStartedGameIdStateRaw(value);
  };
  const startedGameId = startedGameIdState; // Alias for reading
  
  const [gameProgressState, setGameProgressStateRaw] = useState(0); // Progress from 0 to 100
  const setGameProgressRef = useRef(() => {});
  // ✅ DEBUG: Wrapper to log all gameProgress changes
  const setGameProgress = (value, reason = 'unknown') => {
    const stack = new Error().stack;
    const caller = stack?.split('\n')[2]?.trim() || 'unknown';
    console.log(`📊 [Progress] setGameProgress: ${value}% (reason: ${reason})`, {
      previousValue: gameProgressState,
      newValue: value,
      startedGameId: startedGameIdRef.current,
      startedGameIdState: startedGameIdState,
      queueProgress: queueProgress,
      wsExists: !!ws,
      wsReadyState: ws?.readyState,
      caller: caller.substring(0, 100)
    });
    // Force synchronous DOM update so progress bar updates immediately from WebSocket (no F5 needed)
    flushSync(() => {
      setGameProgressStateRaw(value);
    });
  };
  setGameProgressRef.current = setGameProgress; // So WebSocket/polling always call latest setter
  const gameProgress = gameProgressState; // Alias for reading
  const minHealthSeenRef = useRef(30); // Track minimum health seen (starting health is 30)
  const initialHealthRef = useRef(30); // Store initial health for calculation
  const hasReconnectedRef = useRef(false); // Track if we've reconnected (to force progress update)
  const startedGameIdRef = useRef(null); // Ref to track gameId for message filtering
  const reconnectingRef = useRef(false); // Track if we're currently reconnecting to prevent duplicate reconnects
  const hasActiveGameRef = useRef(false); // Track if there's actually an active game (independent of ws state)
  const reconnectTargetGameIdRef = useRef(null); // Track which gameId we're trying to reconnect to
  const queueCompletedRef = useRef(false); // Track if the current queue has completed (prevents reconnecting to new games)
  const navigatingToSpectateRef = useRef(false); // ✅ FIX: Flag to stop all WS activity when navigating to BattleUI
  const [unviewedRewardsCount, setUnviewedRewardsCount] = useState(0); // Count of unviewed rewards
  const [unviewedRewards, setUnviewedRewards] = useState([]); // Unviewed rewards data
  const [gameResult, setGameResult] = useState(null); // 'win' | 'loss' | null - Result for player bot
  const playerBotIdRef = useRef(null); // Store playerBotId to check winner
  const [gameHistory, setGameHistory] = useState([]); // History of past autobattler games
  const [queueProcessing, setQueueProcessing] = useState(false); // Whether queue is being processed
  const [historyLoading, setHistoryLoading] = useState(false); // Loading state for history refresh
  const [queueProgress, setQueueProgress] = useState({ current: 0, total: 0, currentFaction: '' }); // Queue progress tracking
  const queueRef = useRef({ games: [], currentIndex: 0, processing: false }); // Ref for queue state
  const [activeTab, setActiveTab] = useState('setup'); // 'setup', 'history', or 'rewards'
  const [hasOngoingBattles, setHasOngoingBattles] = useState(false); // Track if there are ongoing battles
  const [lastQueueFailure, setLastQueueFailure] = useState(null); // Last error from queue (shown in UI, no logs needed)
  const [resources, setResources] = useState({
    lavender: 0,
    biomass: 0,
    foodRations: 0,
    stone: 0,
    erray: 0
  }); // User resources for bot-vs-bot battles
  const [reservedResources, setReservedResources] = useState({
    lavender: 0,
    biomass: 0,
    foodRations: 0,
    stone: 0,
    erray: 0
  }); // Reserved resources (pending battles)
  
  // Avatar state (from localStorage as fallback - actual avatars come from game state in BattleUI)
  const [selectedGeneral, setSelectedGeneral] = useState(localStorage.getItem('selectedGeneral') || '');
  const [selectedAdvisor, setSelectedAdvisor] = useState(localStorage.getItem('selectedAdvisor') || '');

  // Helper function to calculate total order count from a strategy
  const getStrategyOrderCount = (strategy) => {
    if (!strategy.orders || !Array.isArray(strategy.orders)) return 0;
    return strategy.orders.reduce((total, order) => total + (order.count || 0), 0);
  };

  // Helper function to check if strategy has valid order count (50-100)
  const isValidOrderCount = (strategy) => {
    const orderCount = getStrategyOrderCount(strategy);
    return orderCount >= 50 && orderCount <= 100;
  };

  // Helper function to ensure token is valid before API calls
  const ensureValidToken = async () => {
    let token = localStorage.getItem('token');
    const refreshTokenValue = localStorage.getItem('refreshToken');
    
    if (token && refreshTokenValue) {
      // Check if token is expired or about to expire
      if (!isTokenValid(token) || needsTokenRefresh(token)) {
        try {
          console.log('🔄 Token expired or about to expire, refreshing...');
          token = await refreshToken(refreshTokenValue);
          localStorage.setItem('token', token);
          console.log('✅ Token refreshed successfully');
        } catch (error) {
          console.error('❌ Token refresh failed:', error);
          throw new Error('Token refresh failed. Please refresh the page.');
        }
      }
    } else if (!token) {
      throw new Error('No authentication token. Please log in again.');
    }
    
    return token;
  };

  useEffect(() => {
    const fetchStrategies = async () => {
      try {
        // ✅ FIX: Refresh token if needed before making API calls
        const token = await ensureValidToken();
        
        const res = await fetch(`${API_URL}/my-strategies`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json();
        if (data.error) {
          console.error("Bot battle error:", data.error);
          showNotification(t('battleOverview.errorStartingBot') + ' ' + data.error, 'error', 6000);
          return;
        }

        if (Array.isArray(data)) {
          // Filter strategies to only include those with 50-100 orders
          const validStrategies = data.filter(strategy => isValidOrderCount(strategy));
          setAllStrategies(validStrategies);
          // Validate all strategies
          const validations = {};
          for (const strategy of validStrategies) {
            if (strategy.id) {
              try {
                // ✅ FIX: Use refreshed token for validation requests
                const currentToken = await ensureValidToken();
                const validationRes = await fetch(`${API_URL}/strategy/${strategy.id}/validate`, {
                  headers: {
                    Authorization: `Bearer ${currentToken}`,
                  },
                });
                const validationData = await validationRes.json();
                validations[strategy.id] = validationData;
              } catch (err) {
                console.error(`Failed to validate strategy ${strategy.id}:`, err);
                validations[strategy.id] = { valid: false, insufficientOrders: [] };
              }
            }
          }
          setStrategyValidations(validations);
        } else {
          console.error('Unexpected response format:', data);
          setAllStrategies([]);
        }
      } catch (err) {
        console.error('Failed to fetch strategies:', err);
        if (err.message && (err.message.includes('Token refresh failed') || err.message.includes('No authentication token'))) {
          showNotification(err.message, 'error', 6000);
        }
        setAllStrategies([]);
      }
    };

    fetchStrategies();
  }, []);

  // Restore faction configs from localStorage on mount (before initialization)
  // NOTE: This is ONLY for UI persistence - actual resource costs and spending are calculated
  // and controlled by the backend. localStorage cannot manipulate resource spending.
  useEffect(() => {
    if (factions.length > 0 && factionConfigs.length === 0) {
      try {
        const savedConfigs = localStorage.getItem('autobattler_faction_configs');
        if (savedConfigs) {
          const parsed = JSON.parse(savedConfigs);
          // Validate that saved configs match current factions
          const validConfigs = parsed.filter(saved => 
            factions.some(f => (f.name || f.faction_name) === saved.factionName)
          );
          // Merge with all factions (add missing factions, keep saved ones)
          const factionMap = new Map(validConfigs.map(c => [c.factionName, c]));
          const mergedConfigs = factions.map(f => {
            const factionName = f.name || f.faction_name;
            const saved = factionMap.get(factionName);
            const base = saved || { factionName, strategyId: '', count: 0, aggressive: false };
            return { ...base, aggressive: base.aggressive ?? false };
          });
          setFactionConfigs(mergedConfigs);
          return; // Don't run initialization if we restored from localStorage
        }
      } catch (err) {
        console.error('Failed to restore faction configs from localStorage:', err);
      }
      
      // Initialize faction configs if no saved configs found
      const initialConfigs = factions.map(f => ({
        factionName: f.name || f.faction_name,
        strategyId: '',
        count: 0,
        aggressive: false
      }));
      setFactionConfigs(initialConfigs);
    }
  }, [factions, factionConfigs.length]);

  // Save faction configs to localStorage whenever they change (if they have meaningful values)
  // NOTE: This is ONLY for UI persistence - actual resource reservation/spending happens on backend
  // via /reserve-autobattler-resources and chargeAutobattlerResources() endpoints
  useEffect(() => {
    if (factionConfigs.length > 0) {
      // Only save if there are configs with non-zero counts or selected strategies
      const hasRelevantConfigs = factionConfigs.some(c => c.count > 0 || c.strategyId);
      if (hasRelevantConfigs) {
        try {
          localStorage.setItem('autobattler_faction_configs', JSON.stringify(factionConfigs));
        } catch (err) {
          console.error('Failed to save faction configs to localStorage:', err);
        }
      } else {
        // Clear saved configs if all are empty
        localStorage.removeItem('autobattler_faction_configs');
      }
    }
  }, [factionConfigs]);

  // Fetch user resources and reserved resources
  useEffect(() => {
    const fetchResources = async () => {
      try {
        const token = await ensureValidToken();
        const response = await fetch(`${API_URL}/user-resources`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const resourcesData = await response.json();
          setResources(resourcesData);
        } else {
          console.error('Failed to fetch resources:', response.status);
        }
        
        // Also fetch reserved resources
        const reservedResponse = await fetch(`${API_URL}/reserved-autobattler-resources`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (reservedResponse.ok) {
          const reservedData = await reservedResponse.json();
          setReservedResources(reservedData);
        }
      } catch (err) {
        console.error('Error fetching resources:', err);
      }
    };

    fetchResources();
  }, []);

  // Poll battle progress when we have an active game so the progress bar updates even if WebSocket updates are not received
  useEffect(() => {
    if (!startedGameId || !startedGameIdRef.current) return;
    const gameIdToPoll = startedGameIdRef.current;
    const pollProgress = async () => {
      try {
        const token = await ensureValidToken();
        const res = await fetch(`${API_URL}/autobattler-battle-progress?gameId=${encodeURIComponent(gameIdToPoll)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.progress !== null && data.progress !== undefined) {
          setGameProgressRef.current(data.progress, 'poll');
          if (data.winner && playerBotIdRef.current) {
            setGameResult(data.winner === playerBotIdRef.current ? 'win' : 'loss');
          }
        }
      } catch (err) {
        // Ignore poll errors (e.g. game ended, network)
      }
    };
    pollProgress();
    const interval = setInterval(pollProgress, 2000);
    return () => clearInterval(interval);
  }, [startedGameId]);

  // ✅ FIX: Periodically check for active games and update button state
  useEffect(() => {
    // Only check if we have a startedGameId but no active WebSocket connection
    if (!startedGameId || (ws && ws.readyState === WebSocket.OPEN)) {
      return; // Don't check if no startedGameId or if WebSocket is active
    }

    const checkActiveGame = async () => {
      try {
        const token = await ensureValidToken();
        
        // Check if there's an active game
        const response = await fetch(`${API_URL}/check-active-game`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          // If no active game and we have a startedGameId, clear it to switch button back
          if (!data.hasActiveGame && startedGameId) {
            console.log('🔄 [Periodic Check] No active game found, clearing startedGameId');
            setStartedGameId(null);
            startedGameIdRef.current = null;
            hasActiveGameRef.current = false;
          }
        }
      } catch (err) {
        console.error('Error checking active game:', err);
      }
    };

    // Check immediately and then every 5 seconds
    checkActiveGame();
    const interval = setInterval(checkActiveGame, 5000);

    return () => clearInterval(interval);
  }, [startedGameId, ws]);

  // Fetch factions
  useEffect(() => {
    const fetchFactions = async () => {
      try {
        const currentLanguage = i18nInstance.language || 'en-us';
        const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
        
        const token = await ensureValidToken();
        const response = await fetch(`${API_URL}/factions?lang=${encodeURIComponent(normalizedLanguage)}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const factionsData = await response.json();
          // Filter to only include factions with hero IDs
          const availableFactions = factionsData.filter(f => factionHeroIds[f.name] || factionHeroIds[f.faction_name]);
          setFactions(availableFactions);
        }
      } catch (err) {
        console.error('Failed to fetch factions:', err);
        setFactions([]);
      }
    };
    
    fetchFactions();
  }, [i18nInstance.language]);

  // Fetch faction translations
  useEffect(() => {
    const fetchFactionTranslations = async () => {
      try {
        const currentLanguage = i18nInstance.language || 'en-us';
        const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
        
        const response = await fetch(`${API_URL}/factions?lang=${encodeURIComponent(normalizedLanguage)}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const factions = await response.json();
          const translationMap = {};
          factions.forEach(faction => {
            translationMap[faction.english_name] = faction.name;
          });
          setFactionTranslations(translationMap);
        }
      } catch (err) {
        console.error('Failed to fetch faction translations:', err);
        // Fallback: use English names
        setFactionTranslations({});
      }
    };
    
    fetchFactionTranslations();
  }, [i18nInstance.language]);

  // Helper function to compare rewards arrays (deep comparison by gameId)
  const rewardsEqual = (a, b) => {
    if (!a || !b) return a === b;
    if (a.length !== b.length) return false;
    const aIds = a.map(r => r.gameId).sort();
    const bIds = b.map(r => r.gameId).sort();
    return JSON.stringify(aIds) === JSON.stringify(bIds);
  };
  
  // Fetch unviewed rewards count
  useEffect(() => {
    // Skip fetching if we're in dev mode
    if (isDevModeRef.current) {
      console.log('🎁 DispatchAsset: Skipping real rewards fetch (dev mode)');
      return;
    }

    const fetchUnviewedRewards = async () => {
      try {
        const token = await ensureValidToken();
        const response = await fetch(`${API_URL}/unviewed-autobattler-rewards`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          // Include all wins (even if rewards are empty due to daily limit)
          // Backend already filters for wins only, so we just need to ensure it's an array
          const validRewards = (data.unviewedRewards || []).filter(reward => {
            return reward.outcome === 'win';
          });
          
          // Only update state if rewards actually changed (prevents unnecessary re-renders)
          setUnviewedRewards(prev => {
            if (rewardsEqual(prev, validRewards)) {
              return prev; // Return previous array reference if unchanged
            }
            return validRewards;
          });
          setUnviewedRewardsCount(validRewards.length);
        }
      } catch (err) {
        console.error('Failed to fetch unviewed rewards:', err);
      }
    };
    
    fetchUnviewedRewards();
    // Refresh every 30 seconds to check for new rewards, but only if not on rewards tab
    // When on rewards tab, refresh less frequently (every 2 minutes) to avoid scroll reset
    const interval = setInterval(() => {
      // Only refresh if not actively viewing rewards tab and not in dev mode
      if (activeTab !== 'rewards' && !isDevModeRef.current) {
        fetchUnviewedRewards();
      }
    }, 30000);
    
    // Separate interval for rewards tab (less frequent to avoid scroll issues)
    const rewardsTabInterval = setInterval(() => {
      if (activeTab === 'rewards' && !isDevModeRef.current) {
        fetchUnviewedRewards();
      }
    }, 120000); // 2 minutes when on rewards tab
    
    return () => {
      clearInterval(interval);
      clearInterval(rewardsTabInterval);
    };
  }, [activeTab]);

  // Fetch autobattler game history
  const fetchGameHistory = async (showLoading = false) => {
    try {
      if (showLoading) {
        setHistoryLoading(true);
      }
      const token = await ensureValidToken();
      console.log('🔍 Fetching history from:', `${API_URL}/autobattler-history`);
      const response = await fetch(`${API_URL}/autobattler-history`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log('📡 History API Response status:', response.status, response.statusText);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📊 History API Response data:', JSON.stringify(data, null, 2));
        console.log('📊 History array:', data.history);
        console.log('📊 History array type:', Array.isArray(data.history) ? 'Array' : typeof data.history);
        console.log('📊 History length:', data.history?.length || 0);
        
        if (!data.history) {
          console.error('❌ CRITICAL: data.history is missing! Response:', data);
          setGameHistory([]);
          return;
        }
        
        if (!Array.isArray(data.history)) {
          console.error('❌ CRITICAL: data.history is not an array! Type:', typeof data.history, 'Value:', data.history);
          setGameHistory([]);
          return;
        }
        
        const historyArray = data.history;
        console.log('📊 Setting gameHistory to:', historyArray);
        setGameHistory(historyArray);
        console.log(`✅ History refreshed: ${historyArray.length} games found`);
        
        // Force a re-render check
        setTimeout(() => {
          console.log('📊 gameHistory state after set (delayed check):', historyArray);
        }, 100);
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to fetch game history:', response.status, response.statusText);
        console.error('❌ Error response body:', errorText);
        setGameHistory([]);
      }
    } catch (err) {
      console.error('❌ Exception fetching game history:', err);
      console.error('❌ Error stack:', err.stack);
      setGameHistory([]);
    } finally {
      if (showLoading) {
        setHistoryLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchGameHistory();
    // Refresh every 60 seconds
    const interval = setInterval(fetchGameHistory, 60000);
    return () => clearInterval(interval);
  }, []);

  // Track if user has viewed the Rewards tab
  const hasViewedRewardsTabRef = useRef(false);
  
  // Mark rewards as viewed when user switches away from Rewards tab (after having viewed it)
  useEffect(() => {
    if (activeTab === 'rewards') {
      // User is on Rewards tab - mark that they've viewed it
      hasViewedRewardsTabRef.current = true;
    } else if (activeTab !== 'rewards' && hasViewedRewardsTabRef.current && unviewedRewards.length > 0) {
      // User switched away from Rewards tab after viewing it, mark rewards as viewed
      handleMarkRewardsAsViewed();
      hasViewedRewardsTabRef.current = false; // Reset for next time
    }
  }, [activeTab]);

  // Check for active autobattler game on mount and reconnect if needed
  useEffect(() => {
    const checkAndReconnectActiveGame = async () => {
      // Don't reconnect if we already have a WebSocket connection
      if (ws || startedGameId) return;
      
      try {
        const token = await ensureValidToken();
        
        // ✅ CRITICAL: First check queue status to see if there's actually a queue
        // ✅ CRITICAL: Server status is the source of truth - no localStorage checks
        let hasActiveQueue = false; // Initialize to false - will be updated if queue status check succeeds
        try {
          const queueStatusResponse = await fetch(`${API_URL}/autobattler-queue-status`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          if (queueStatusResponse.ok) {
            const queueStatus = await queueStatusResponse.json();
            const active = parseInt(queueStatus.active || 0); // Use active count from server (only pending + processing)
            hasActiveQueue = (active > 0);
          }
        } catch (fetchError) {
          // Network error or fetch failed - hasActiveQueue remains false
          console.error('Failed to fetch queue status:', fetchError);
        }
        
        // Update ongoing battles state
        setHasOngoingBattles(hasActiveQueue);
        
        // ✅ CRITICAL: Server status is the source of truth - no localStorage checks
        if (!hasActiveQueue) {
            console.log('ℹ️ No active queue on server, clearing all state');
            setQueueProcessing(false);
            queueRef.current.processing = false;
            queueCompletedRef.current = false; // Reset completion flag
            // ✅ CRITICAL: Clear queue progress when no active queue
            setQueueProgress({ current: 0, total: 0, currentFaction: '' });
            // ✅ CRITICAL: Clear WebSocket so buttons are enabled
            setWs(prevWs => {
              if (prevWs) {
                try {
                  prevWs.close();
                } catch {}
                localStorage.setItem('autobattler_has_active_ws', 'false');
              }
              return null;
            });
            setStartedGameId(null);
            startedGameIdRef.current = null;
            hasActiveGameRef.current = false;
            setHasOngoingBattles(false); // Clear ongoing battles state
            setGameProgress(0, 'Mount: No active queue');
            return; // Don't try to reconnect if there's no queue
          }
        
        // ✅ CRITICAL: First check for current processing game from queue (most accurate)
        const currentGameResponse = await fetch(`${API_URL}/autobattler-current-game`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        let gameIdToReconnect = null;
        if (currentGameResponse.ok) {
          const currentGameData = await currentGameResponse.json();
          if (currentGameData.gameId) {
            gameIdToReconnect = currentGameData.gameId;
            console.log('🔄 Found current processing game from queue:', gameIdToReconnect);
          }
        }
        
        // Fallback to check-active-game if no queue game found
        if (!gameIdToReconnect) {
          const response = await fetch(`${API_URL}/check-active-game`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.hasActiveGame && data.gameId && data.gameId.includes('_autobattler_')) {
              gameIdToReconnect = data.gameId;
              console.log('🔄 Found active game from check-active-game:', gameIdToReconnect);
            }
          }
        }
        
        if (gameIdToReconnect) {
          // ✅ CRITICAL: Don't restore queue progress from localStorage - WebSocket messages are the source of truth
          // Queue progress will be set when WebSocket messages arrive with queueProgress data
          setQueueProcessing(true);
          queueRef.current.processing = true;
          
          // ✅ CRITICAL: Try to get current game state immediately via REST API
          // This allows us to show progress immediately without waiting for WebSocket
          (async () => {
            try {
              const gameResponse = await fetch(`${API_URL}/game/${gameIdToReconnect}`, {
                headers: {
                  Authorization: `Bearer ${token}`
                }
              });
              
              if (gameResponse.ok) {
                const gameData = await gameResponse.json();
                const gameState = gameData.gameState;
                
                if (gameState && gameState.isAutobattler && gameState.playerBotId && gameState.opponentBotId) {
                  const playerBotId = gameState.playerBotId;
                  const opponentBotId = gameState.opponentBotId;
                  
                  // Set startedGameId immediately so progress bar shows
                  startedGameIdRef.current = gameIdToReconnect;
                  setStartedGameId(gameIdToReconnect);
                  playerBotIdRef.current = playerBotId;
                  
                  // Initialize health refs
                  initialHealthRef.current = 30;
                  
                  // Calculate progress immediately from current game state
                  if (gameState.state && gameState.state[playerBotId] && gameState.state[opponentBotId]) {
                    const playerHealth = gameState.state[playerBotId]?.health ?? 30;
                    const opponentHealth = gameState.state[opponentBotId]?.health ?? 30;
                    const currentMinHealth = Math.min(playerHealth, opponentHealth);
                    
                    minHealthSeenRef.current = currentMinHealth;
                    
                    if (currentMinHealth < 30) {
                      // ✅ CRITICAL: Only set progress to 100% when game is actually complete (winner is set)
                      // If health is <= 0 but winner is not set, cap progress at 99% (game is almost done but not complete)
                      // Health can go negative, so we check <= 0, not === 0
                      let progress = Math.min(100, Math.max(0, ((30 - currentMinHealth) / 30) * 100));
                      if (currentMinHealth <= 0 && !gameState.winner) {
                        progress = 99; // Game is almost done but not complete yet (health is 0 or negative, but winner not set)
                        console.log(`⚠️ [Mount] Health is ${currentMinHealth} (<= 0) but winner not set yet - capping progress at 99%`);
                      }
                      setGameProgress(progress, `Mount: Immediate progress from REST API (health=${currentMinHealth}, winner=${gameState.winner || 'none'})`);
                      console.log(`📊 [Mount] Immediate progress from REST API: health=${currentMinHealth}, progress=${progress}%, winner=${gameState.winner || 'none'}`);
                    } else {
                      setGameProgress(0, 'Mount: No active queue');
                    }
                    
                    // ✅ CRITICAL: Check if game ended - ONLY set progress to 100% when winner is actually set
                    if (gameState.winner) {
                      setGameProgress(100, 'Mount: Game already ended (REST API)');
                      if (gameState.winner === playerBotId) {
                        setGameResult('win');
                      } else {
                        setGameResult('loss');
                      }
                    }
                  }
                  
                  // ✅ FIX: Fetch queue progress immediately from API so battle number shows correctly on refresh
                  try {
                    const queueProgressResponse = await fetch(`${API_URL}/autobattler-queue-progress?gameId=${encodeURIComponent(gameIdToReconnect)}`, {
                      headers: {
                        Authorization: `Bearer ${token}`
                      }
                    });
                    
                    if (queueProgressResponse.ok) {
                      const queueProgressData = await queueProgressResponse.json();
                      if (queueProgressData.queueProgress) {
                        console.log(`📋 [Mount] Fetched queue progress from API: ${queueProgressData.queueProgress.current}/${queueProgressData.queueProgress.total}`);
                        setQueueProgress({
                          current: queueProgressData.queueProgress.current,
                          total: queueProgressData.queueProgress.total,
                          currentFaction: queueProgress.currentFaction || ''
                        });
                      }
                    }
                  } catch (err) {
                    console.error('Failed to fetch queue progress from API:', err);
                    // Continue - WebSocket will send queue progress later
                  }
                }
              }
            } catch (err) {
              console.error('Failed to fetch game state immediately:', err);
              // Continue with WebSocket reconnect anyway
            }
          })();
          
          reconnectToActiveGame(gameIdToReconnect, token);
        } else if (hasActiveQueue) {
          // Queue exists but no active game - set processing flag, queue progress will come from WebSocket
          setQueueProcessing(true);
          queueRef.current.processing = true;
        }
      } catch (err) {
        console.error('Failed to check for active game:', err);
        // On error, clear WebSocket state to unblock UI
        // If there's actually a queue, it will be restored by polling
        setWs(prevWs => {
          if (prevWs) {
            try {
              prevWs.close();
            } catch {}
            localStorage.setItem('autobattler_has_active_ws', 'false');
          }
          return null;
        });
        setStartedGameId(null);
        startedGameIdRef.current = null;
            setGameProgress(0, 'Mount: No active queue');
        setQueueProcessing(false);
        queueRef.current.processing = false;
        queueCompletedRef.current = false; // Reset completion flag
        // ✅ CRITICAL: ALWAYS clear WebSocket on error - this unblocks the UI
        console.log(`🔄 [Error] Clearing WebSocket due to error`);
        setWs(prevWs => {
          if (prevWs) {
            try {
              prevWs.close();
            } catch {}
            localStorage.setItem('autobattler_has_active_ws', 'false');
          }
          return null; // Always return null to clear ws
        });
      }
    };
    
    checkAndReconnectActiveGame();
    
    // ✅ CRITICAL: Poll for active queue to detect when games end and clear state
    // Queue progress comes from WebSocket messages, not polling
    const pollInterval = setInterval(async () => {
      try {
        const token = await ensureValidToken();
        
        // Check queue status
        const queueStatusResponse = await fetch(`${API_URL}/autobattler-queue-status`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (queueStatusResponse.ok) {
          const queueStatus = await queueStatusResponse.json();
          const active = parseInt(queueStatus.active || 0); // Only pending + processing
          const currentGame = queueStatus.currentGame || null; // Get current game with started_at/completed_at
          // Show last failure in UI so user doesn't need server logs
          setLastQueueFailure(queueStatus.lastFailure || null);
          
          // ✅ CRITICAL: Use started_at and completed_at from queue to reliably know game state
          if (currentGame) {
            const gameCompleted = currentGame.completedAt !== null && currentGame.completedAt !== undefined;
            const gameStarted = currentGame.startedAt !== null && currentGame.startedAt !== undefined;
            
            console.log(`📊 [Poll] Current game:`, {
              gameId: currentGame.gameId,
              startedAt: currentGame.startedAt,
              completedAt: currentGame.completedAt,
              status: currentGame.status,
              gameCompleted,
              gameStarted
            });
            
            // ✅ CRITICAL: If game has completed_at, it's done - don't try to reconnect
            if (gameCompleted && currentGame.gameId === startedGameIdRef.current) {
              console.log(`✅ [Poll] Game ${currentGame.gameId} is completed (completedAt: ${currentGame.completedAt})`);
              // Game is done - wait for next game to start (started_at will be set)
            }
            
            // ✅ CRITICAL: Only switch to next game when started_at is provided (game actually started)
            if (gameStarted && currentGame.gameId && currentGame.gameId !== startedGameIdRef.current) {
              console.log(`🔄 [Poll] New game started (startedAt: ${currentGame.startedAt}): ${currentGame.gameId}, switching from ${startedGameIdRef.current}`);
              // New game has started - switch to it
              reconnectToActiveGame(currentGame.gameId, token);
            }
          }
          
          // Update ongoing battles state
          setHasOngoingBattles(active > 0);
        } else {
          // API returned non-ok status - assume no active queue
          setHasOngoingBattles(false);
          
          // ✅ CRITICAL: Only set queueProcessing to true if there's actually an active queue
          if (active > 0) {
            setQueueProcessing(prev => {
              if (!prev) {
                console.log(`📋 [Poll] Setting queueProcessing to true (active: ${active})`);
                queueRef.current.processing = true;
                return true;
              }
              return prev;
            });
          } else {
            // No active queue - clear everything
            setQueueProcessing(prev => {
              if (prev) {
                console.log(`📋 [Poll] Clearing queueProcessing (no active queue)`);
                queueRef.current.processing = false;
                queueCompletedRef.current = false; // Reset completion flag
                return false;
              }
              return prev;
            });
            
            // ✅ CRITICAL: Only clear state if we're not actively processing a queue locally
            // If queueRef.current.processing is true, we're in the middle of processing games
            // and should keep the queue progress even if the API says no active queue
            if (!queueRef.current.processing) {
              console.log(`🔄 [Poll] No active queue (active: ${active}), clearing WebSocket, game state, and queue progress`);
              setGameProgress(0, 'Poll: No active queue');
              setStartedGameId(null);
              startedGameIdRef.current = null;
              hasActiveGameRef.current = false;
              // ✅ CRITICAL: Clear queue progress when no active queue (WebSocket is source of truth)
              setQueueProgress({ current: 0, total: 0, currentFaction: '' });
            } else {
              console.log(`🔄 [Poll] No active queue on server (active: ${active}), but processing locally - keeping queue progress`);
            }
            setWs(prevWs => {
              if (prevWs) {
                console.log(`🔄 [Poll] Clearing WebSocket - no active queue`);
                try {
                  prevWs.close();
                } catch {}
                localStorage.setItem('autobattler_has_active_ws', 'false');
              }
              return null;
            });
          }
          
          // ✅ CRITICAL: Only try to reconnect if there's actually an active queue AND we're processing locally
          // AND the queue hasn't completed yet. This prevents reconnecting to games from other queues or leftover games
          // ✅ CRITICAL: Use started_at from currentGame to know when game actually started
          if (active > 0 && queueRef.current.processing && !queueCompletedRef.current) {
            const processing = parseInt(queueStatus.processing || 0);
            if (processing > 0 && currentGame && currentGame.startedAt) {
              // ✅ CRITICAL: Only reconnect if game has started_at (game actually started)
              // AND we're not already connected to this game
              const currentGameId = startedGameIdRef.current;
              const hasActiveWs = ws && ws.readyState === WebSocket.OPEN;
              
              // ✅ CRITICAL: Only reconnect if:
              // 1. Game has started_at (actually started)
              // 2. We're not already connected to this game
              // 3. Game is not completed (completedAt is null)
              if (currentGame.startedAt && 
                  !currentGame.completedAt &&
                  (!hasActiveWs || !currentGameId || currentGameId !== currentGame.gameId) && 
                  !(ws && ws.readyState === WebSocket.OPEN && currentGameId === currentGame.gameId)) {
                console.log(`🔄 [Poll] Game started (startedAt: ${currentGame.startedAt}): ${currentGame.gameId}, reconnecting... (hasActiveWs: ${hasActiveWs}, currentGameId: ${currentGameId})`);
                reconnectToActiveGame(currentGame.gameId, token);
              }
            }
          }
        }
      } catch (err) {
        console.error('Failed to poll queue status:', err);
        // On error, assume no active queue to prevent button from being stuck disabled
        setHasOngoingBattles(false);
      }
    }, 1000); // Poll every 1 second
    
    return () => {
      clearInterval(pollInterval);
    };
  }, []); // Only run on mount - polling will check state via refs and localStorage

  // Reconnect to an active autobattler game
  const reconnectToActiveGame = (gameId, token) => {
    // ✅ FIX: Don't reconnect if user is navigating to spectate
    if (navigatingToSpectateRef.current) {
      console.log(`🛑 [Reconnect] Blocked - user is navigating to spectate BattleUI`);
      return;
    }
    
    // ✅ CRITICAL: Update target gameId ref
    reconnectTargetGameIdRef.current = gameId;
    
    // ✅ CRITICAL: Prevent multiple simultaneous reconnects to the same game
    if (reconnectingRef.current && reconnectTargetGameIdRef.current === gameId) {
      console.log(`🔄 [Reconnect] Already reconnecting to gameId: ${gameId}, skipping duplicate call`);
      return;
    }
    
    // ✅ CRITICAL: If reconnecting to a different game, allow it (update target)
    if (reconnectingRef.current && reconnectTargetGameIdRef.current !== gameId) {
      console.log(`🔄 [Reconnect] Updating reconnect target from ${reconnectTargetGameIdRef.current} to ${gameId}`);
      reconnectTargetGameIdRef.current = gameId;
      // Continue with reconnect to new game
    }
    
    // ✅ CRITICAL: Check if we're already connected to this game (use functional update to avoid stale closure)
    let shouldReconnect = true;
    setWs(prevWs => {
      // Don't create a new WebSocket if we already have one connected to this game
      if (prevWs && prevWs.readyState === WebSocket.OPEN && startedGameIdRef.current === gameId) {
        console.log(`🔄 [Reconnect] Already connected to gameId: ${gameId}, skipping reconnect`);
        shouldReconnect = false;
        return prevWs; // Return existing WebSocket
      }
      
      // Close existing WebSocket if it's connected to a different game
      if (prevWs && prevWs.readyState === WebSocket.OPEN && startedGameIdRef.current !== gameId) {
        console.log(`🔄 [Reconnect] Closing existing WebSocket (connected to ${startedGameIdRef.current}) before reconnecting to ${gameId}`);
        try {
          prevWs.close();
        } catch {}
      }
      
      return prevWs; // Keep existing for now
    });
    
    if (!shouldReconnect) {
      return; // Already connected, don't create new socket
    }
    
    // Set reconnecting flag and target gameId
    reconnectingRef.current = true;
    reconnectTargetGameIdRef.current = gameId;
    
    // Create new WebSocket
    console.log(`🔄 [Reconnect] Starting reconnect to gameId: ${gameId}`);
    console.log(`🔍 [Reconnect] State before creating socket:`, {
      currentStartedGameId: startedGameIdRef.current,
      hasWs: !!ws,
      wsReadyState: ws?.readyState,
      gameProgress: gameProgress,
      queueProgress: queueProgress
    });
    const socket = new window.WebSocket(WS_URL);
    
    // Store socket immediately
    setWs(socket);
    localStorage.setItem('autobattler_has_active_ws', 'true');
    console.log(`🔍 [Reconnect] WebSocket created, readyState: ${socket.readyState}`);
    
    socket.onopen = () => {
      console.log(`🔄 [Reconnect] WebSocket opened, sending auth for gameId: ${gameId}`);
      console.log(`🔍 [Reconnect] Socket state on open:`, {
        readyState: socket.readyState,
        startedGameId: startedGameIdRef.current,
        hasToken: !!token
      });
      const currentLanguage = localStorage.getItem('i18nextLng') || 'en-us';
      const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
      socket.send(JSON.stringify(getWebSocketAuthMessage(token, normalizedLanguage)));
      console.log(`🔍 [Reconnect] Auth message sent`);
    };
    
    socket.onerror = (error) => {
      console.error(`❌ [Reconnect] WebSocket error:`, error);
      console.log(`🔍 [Reconnect] Error state:`, {
        readyState: socket.readyState,
        gameId: gameId,
        startedGameId: startedGameIdRef.current
      });
    };
    
    socket.onclose = (event) => {
      console.log(`🔄 [Reconnect] WebSocket closed:`, {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
        gameId: gameId,
        startedGameId: startedGameIdRef.current
      });
    };

    socket.onmessage = (event) => {
      // ✅ FIX: Bail out immediately if user is navigating to spectate
      // This prevents the closure from processing messages after navigation
      if (navigatingToSpectateRef.current) {
        console.log(`🛑 [Reconnect WS] Ignoring message - user navigating to spectate`);
        // Also close this socket since we're navigating away
        try { socket.close(); } catch {}
        return;
      }
      
      try {
        const data = JSON.parse(event.data);
        
        // ✅ DEBUG: Log ALL WebSocket messages for game 2 debugging
        console.log(`🔍 [Reconnect WS] Message received:`, {
          type: data.type,
          gameId: data.gameId,
          hasGameState: !!data.gameState,
          hasWinner: data.gameState?.winner,
          playerBotId: data.gameState?.playerBotId,
          opponentBotId: data.gameState?.opponentBotId,
          hasState: !!data.gameState?.state,
          currentGameId: startedGameIdRef.current,
          wsReadyState: socket.readyState
        });
        
        if (data.type === 'auth-success') {
          console.log(`🔍 [Reconnect WS] Auth success, sending reconnect for gameId: ${gameId}`);
          socket.send(JSON.stringify({
            type: 'reconnect',
            gameId: gameId
          }));
          
          // ✅ CRITICAL: Set timeout to reset reconnecting flag if no response received
          // This prevents getting stuck in "already reconnecting" state
          setTimeout(() => {
            if (reconnectingRef.current && reconnectTargetGameIdRef.current === gameId) {
              console.warn(`⚠️ [Reconnect] Timeout waiting for reconnect-success for gameId: ${gameId}, resetting reconnecting flag`);
              reconnectingRef.current = false;
              reconnectTargetGameIdRef.current = null;
            }
          }, 10000); // 10 second timeout
          
          return;
        }
        
        // Handle reconnect-success OR autobattler-started (both contain game state)
        if ((data.type === 'reconnect-success' || data.type === 'autobattler-started') && data.gameState) {
          const gameState = data.gameState;
          
          // ✅ CRITICAL: Use gameId from the message, not the parameter
          // The server might have a different gameId than what we requested
          const actualGameId = data.gameId || gameId;
          console.log(`✅ [Reconnect WS] Reconnected to autobattler game (${data.type}): requested=${gameId}, actual=${actualGameId}`);
          console.log(`🔍 [Reconnect WS] Game state details:`, {
            hasState: !!gameState.state,
            playerBotId: gameState.playerBotId,
            opponentBotId: gameState.opponentBotId,
            winner: gameState.winner,
            isAutobattler: gameState.isAutobattler,
            spectatorUserId: gameState.spectatorUserId
          });
          
          // Store the actual gameId from the server response
          startedGameIdRef.current = actualGameId;
          hasActiveGameRef.current = true; // Mark as active game
          setStartedGameId(actualGameId);
          
          // ✅ FIX: Fetch queue progress immediately from API so battle number shows correctly on refresh
          // This ensures the display shows the correct battle number even before WebSocket sends it
          (async () => {
            try {
              const currentToken = await ensureValidToken();
              const queueProgressResponse = await fetch(`${API_URL}/autobattler-queue-progress?gameId=${encodeURIComponent(actualGameId)}`, {
                headers: {
                  Authorization: `Bearer ${currentToken}`
                }
              });
              
              if (queueProgressResponse.ok) {
                const queueProgressData = await queueProgressResponse.json();
                if (queueProgressData.queueProgress) {
                  console.log(`📋 [Reconnect WS] Fetched queue progress from API: ${queueProgressData.queueProgress.current}/${queueProgressData.queueProgress.total}`);
                  setQueueProgress({
                    current: queueProgressData.queueProgress.current,
                    total: queueProgressData.queueProgress.total,
                    currentFaction: queueProgress.currentFaction || ''
                  });
                }
              }
            } catch (err) {
              console.error('Failed to fetch queue progress from API:', err);
              // Continue - WebSocket will send queue progress later
            }
          })();
          
          // ✅ CRITICAL: Reset health refs for new game (game 2, 3, etc.)
          // This ensures progress bar starts correctly for each new game
          minHealthSeenRef.current = 30;
          initialHealthRef.current = 30;
          hasReconnectedRef.current = true; // Flag to force first progress update
            setGameProgress(0, 'Mount: No active queue'); // Reset progress for new game
          
          // ✅ CRITICAL: Don't restore queue progress from localStorage - WebSocket messages are the source of truth
          // Queue progress will be set when WebSocket messages arrive with queueProgress data
          setQueueProcessing(true);
          queueRef.current.processing = true;
          
          // Extract bot IDs and store them
          const playerBotId = gameState.playerBotId;
          const opponentBotId = gameState.opponentBotId;
          
          if (playerBotId && opponentBotId) {
            playerBotIdRef.current = playerBotId;
            
            // ✅ CRITICAL: Initialize health refs for autobattler (always 30)
            initialHealthRef.current = 30;
            
            // ✅ CRITICAL: Try to get current health from reconnect state
            // Always set hasReconnectedRef to ensure first game-update updates progress
            if (gameState.state && gameState.state[playerBotId] && gameState.state[opponentBotId]) {
              const playerHealth = gameState.state[playerBotId]?.health ?? 30;
              const opponentHealth = gameState.state[opponentBotId]?.health ?? 30;
              const currentMinHealth = Math.min(playerHealth, opponentHealth);
              
              // Set minHealthSeenRef to current health (or 30 if game just started)
              minHealthSeenRef.current = currentMinHealth;
              
              // Calculate progress if health is less than 30 (game has progressed)
              if (currentMinHealth < 30) {
                // ✅ CRITICAL: Only set progress to 100% when game is actually complete (winner is set)
                // If health is <= 0 but winner is not set, cap progress at 99% (game is almost done but not complete)
                // Health can go negative, so we check <= 0, not === 0
                let progress = Math.min(100, Math.max(0, ((30 - currentMinHealth) / 30) * 100));
                if (currentMinHealth <= 0 && !gameState.winner) {
                  progress = 99; // Game is almost done but not complete yet (health is 0 or negative, but winner not set)
                  console.log(`⚠️ [Reconnect] Health is ${currentMinHealth} (<= 0) but winner not set yet - capping progress at 99%`);
                }
                setGameProgress(progress, `Reconnect: Found progress from gameState (health=${currentMinHealth}, winner=${gameState.winner || 'none'})`);
                console.log(`📊 Reconnect: Found progress from gameState - health=${currentMinHealth}, progress=${progress}%, winner=${gameState.winner || 'none'}`);
              } else {
                // Game just started - set progress to 0
                setGameProgress(0, 'Mount: No active queue and no local queue info');
                console.log(`📊 Reconnect: Game at start (health=30), progress=0%`);
              }
              
              // ✅ CRITICAL: Always set hasReconnectedRef to ensure first game-update forces progress update
              // This handles cases where reconnect-success has stale data or game progresses between reconnect and first update
              hasReconnectedRef.current = true;
            } else {
              // No state available - initialize and wait for game-update
              minHealthSeenRef.current = 30;
              setGameProgress(0, 'Mount: No active queue and no local queue info');
              hasReconnectedRef.current = true;
              console.log(`📊 Reconnect: No gameState.state available, waiting for game-update`);
            }
            
            // Check if game already ended
            if (gameState.winner) {
              console.log(`⚠️ [Reconnect] Game ${gameId} already completed (winner: ${gameState.winner}), skipping reconnect - queue will handle next game`);
              setGameProgress(100);
              if (gameState.winner === playerBotId) {
                setGameResult('win');
              } else {
                setGameResult('loss');
              }
              // ✅ CRITICAL: Don't reconnect to completed games - close socket and let queue handle next game
              try { socket.close(); } catch {}
              setWs(null);
              localStorage.setItem('autobattler_has_active_ws', 'false');
              reconnectingRef.current = false; // Clear reconnecting flag
              return;
            }
          }
          
          // Set up WebSocket to listen for updates
          setWs(socket);
          localStorage.setItem('autobattler_has_active_ws', 'true');
          reconnectingRef.current = false; // Clear reconnecting flag
          return;
        }
        
        if (data.type === 'error') {
          console.error('❌ Reconnect error:', data.message);
          try { socket.close(); } catch {}
          setWs(null);
          localStorage.setItem('autobattler_has_active_ws', 'false');
          reconnectingRef.current = false; // Clear reconnecting flag
          reconnectTargetGameIdRef.current = null; // Clear target
          return;
        }
        
          // ✅ CRITICAL: Update queue progress from WebSocket message (source of truth)
          // This can override the initial total set from POST response if there are old queue items
          // ✅ FIX: Check for queueProgress.total (not current) because current can be 0
          // ✅ WAIT: We wait for WebSocket to send queueProgress - no API fallbacks
          // ✅ DEBUG: Log ALL messages to see if queueProgress is included
          if (data.queueProgress) {
            console.log(`📋 [Reconnect WS] Message has queueProgress:`, {
              type: data.type,
              current: data.queueProgress.current,
              total: data.queueProgress.total,
              currentType: typeof data.queueProgress.current,
              hasTotal: !!data.queueProgress.total
            });
          }
          
          if (data.queueProgress && typeof data.queueProgress.current === 'number' && data.queueProgress.total) {
            const previousTotal = queueProgress.total;
            const previousCurrent = queueProgress.current;
            console.log(`📋 [Reconnect WS] ✅ Received queue progress: ${data.queueProgress.current}/${data.queueProgress.total} (was: ${previousCurrent}/${previousTotal})`);
            if (previousTotal > 0 && previousTotal !== data.queueProgress.total) {
              console.log(`⚠️ [Reconnect WS] WebSocket total (${data.queueProgress.total}) differs from initial total (${previousTotal}), using WebSocket value (handles old queue items)`);
            }
            setQueueProgress({
              current: data.queueProgress.current,
              total: data.queueProgress.total,
              currentFaction: queueProgress.currentFaction || ''
            });
          } else if (data.queueProgress) {
            // queueProgress exists but doesn't meet our conditions
            console.warn(`⚠️ [Reconnect WS] queueProgress exists but invalid:`, {
              current: data.queueProgress.current,
              currentType: typeof data.queueProgress.current,
              total: data.queueProgress.total,
              hasTotal: !!data.queueProgress.total
            });
          } else if (queueProgress.total > 0 && queueProgress.current === 0) {
            // Log that we're waiting for WebSocket to send queueProgress
            console.log(`⏳ [Reconnect WS] Waiting for WebSocket to send queueProgress (message type: ${data.type}, current: ${queueProgress.current}, total: ${queueProgress.total})`);
          }
        
        // Handle game state updates (same as in startSingleGame)
        // Also handle autobattler-started in case it comes through here
        if ((data.type === 'game-update' || data.type === 'turn-played' || data.type === 'autobattler-started') && data.gameState) {
          // ✅ CRITICAL: If we're reconnecting and receive a game state message, the reconnect was successful
          if (reconnectingRef.current && reconnectTargetGameIdRef.current === gameId) {
            console.log(`✅ [Reconnect WS] Reconnect successful - received ${data.type} for gameId: ${gameId}`);
            reconnectingRef.current = false; // Clear reconnecting flag
            reconnectTargetGameIdRef.current = null; // Clear target
            startedGameIdRef.current = gameId;
            hasActiveGameRef.current = true;
            setStartedGameId(gameId);
            setQueueProcessing(true);
            queueRef.current.processing = true;
            
            // ✅ CRITICAL: Wait for WebSocket to send queueProgress - it's the source of truth
            // Don't use fallbacks - WebSocket will send it in the next message
            if (queueProgress.total > 0 && queueProgress.current === 0) {
              console.log(`📋 [Reconnect] queueProgress.current is 0, waiting for WebSocket to send queueProgress...`);
            }
          }
          
          const currentGameId = startedGameIdRef.current;
          const messageGameId = data.gameId || (data.gameState?.gameId);
          
          console.log(`🔍 [Reconnect WS] Processing ${data.type} message:`, {
            messageGameId: messageGameId,
            currentGameId: currentGameId,
            targetGameId: gameId,
            hasGameState: !!data.gameState,
            hasState: !!data.gameState?.state,
            playerBotId: data.gameState?.playerBotId,
            opponentBotId: data.gameState?.opponentBotId,
            winner: data.gameState?.winner
          });
          
          // ✅ CRITICAL: Use ref for target gameId (can be updated if we're reconnecting to next game)
          let targetGameId = reconnectTargetGameIdRef.current || gameId;
          
          // ✅ CRITICAL: If message is for a different game but we're in a queue, it might be the next game
          // Update target if this is an autobattler game and we're processing a queue
          if (messageGameId && messageGameId.includes('_autobattler_') && queueRef.current.processing) {
            // If we don't have a current game or current game completed, and this is a different game, it's the next game
            const hasNoCurrentGame = !currentGameId || !startedGameIdRef.current;
            const currentGameCompleted = currentGameId && data.gameState?.winner !== null && data.gameState?.winner !== undefined;
            
            if ((hasNoCurrentGame || currentGameCompleted) && messageGameId !== targetGameId) {
              console.log(`🔄 [Reconnect] Updating reconnect target to next game: ${messageGameId} (was: ${targetGameId}, hasNoCurrent: ${hasNoCurrentGame}, currentCompleted: ${currentGameCompleted})`);
              reconnectTargetGameIdRef.current = messageGameId;
              targetGameId = messageGameId; // Update local variable for this message
            }
          }
          
          // ✅ CRITICAL: Only process messages for the target gameId we're reconnecting to
          // This prevents processing messages for games 3, 4, etc. when we're only reconnecting to game 2
          // Check this FIRST before any gameId updates
          const isTargetGame = messageGameId === targetGameId || (!messageGameId && currentGameId === targetGameId);
          if (!isTargetGame && messageGameId && messageGameId.includes('_autobattler_')) {
            console.log(`⏭️ [Reconnect] Ignoring ${data.type} message for different game (gameId: ${messageGameId}, target: ${targetGameId}, current: ${currentGameId}) - not the game we're reconnecting to`);
            return; // Ignore messages for games we're not reconnecting to
          }
          
          // ✅ CRITICAL: Check if current game has completed and we're in a queue
          // If so, ignore messages for different games (similar to startSingleGame)
          const gameCompleted = data.gameState?.winner !== null && data.gameState?.winner !== undefined;
          const isInQueue = queueRef.current.processing;
          const isDifferentGame = messageGameId && currentGameId && messageGameId !== currentGameId;
          
          if (gameCompleted && isInQueue && isDifferentGame) {
            console.log(`⏭️ [Reconnect] Ignoring ${data.type} message for next game (gameId: ${messageGameId}, current: ${currentGameId}, target: ${targetGameId}) - current game completed, queue will handle next game`);
            return; // Ignore this message - it's for the next game
          }
          
          // ✅ CRITICAL: If we have a gameId in the message, use it (it's the source of truth)
          // This fixes the issue where reconnect-success has one gameId but turn-played has another
          // Update target if it matches (using ref to allow updates)
          // targetGameId is already defined above
          if (messageGameId && messageGameId.includes('_autobattler_') && messageGameId === targetGameId) {
            // Always update gameId from turn-played/game-update messages (they're the source of truth)
            if (!currentGameId || currentGameId !== messageGameId) {
              if (!currentGameId) {
                console.log(`🎮 [Reconnect] ✅ GAME STARTED: ${messageGameId} at ${new Date().toISOString()}`);
              }
              console.log(`📊 [Reconnect WS] Updating gameId from ${data.type}: ${messageGameId} (was: ${currentGameId})`);
              startedGameIdRef.current = messageGameId;
              setStartedGameId(messageGameId);
            }
          }
          
          // ✅ CRITICAL: Process autobattler game updates, but only for the target game
          // This handles the case where reconnect-success has wrong gameId but turn-played has correct one
          // targetGameId is already defined above
          const shouldProcess = (messageGameId === targetGameId) || 
                               (!messageGameId && currentGameId === targetGameId) ||
                               (data.gameState && data.gameState.isAutobattler && messageGameId === targetGameId);
          
          if (shouldProcess) {
            const gameState = data.gameState;
            const playerBotId = gameState.playerBotId;
            const opponentBotId = gameState.opponentBotId;
            
            // ✅ CRITICAL: Queue progress ONLY comes from WebSocket messages (handled above at line 1082-1090)
            // No localStorage or API-based calculations - WebSocket is the single source of truth
            
            if (playerBotId && opponentBotId && gameState.state) {
              // ✅ CRITICAL: Store playerBotId if not set
              if (!playerBotIdRef.current) {
                playerBotIdRef.current = playerBotId;
              }
              
              // ✅ CRITICAL: Always reset health refs when reconnecting to ensure fresh start
              // This is especially important for game 2+ where refs might have values from game 1
              // Check if this is a NEW game (different gameId) - if so, reset everything
              const currentGameId = startedGameIdRef.current;
              const isNewGame = currentGameId && data.gameId && currentGameId !== data.gameId;
              
              // ✅ CRITICAL: If minHealthSeen is 0, it means we're coming from a completed game
              // Reset to 30 to start fresh for the new game
              if (hasReconnectedRef.current || isNewGame || minHealthSeenRef.current === 0 || minHealthSeenRef.current < 0) {
                // Reset for new game
                const oldMinHealth = minHealthSeenRef.current;
                initialHealthRef.current = 30;
                minHealthSeenRef.current = 30;
                console.log(`📊 [Reconnect] Reset health refs for new game (hasReconnected=${hasReconnectedRef.current}, isNewGame=${isNewGame}, oldMinHealth=${oldMinHealth})`);
              } else if (!initialHealthRef.current) {
                initialHealthRef.current = 30;
              }
              
              // ✅ CRITICAL: Also ensure minHealthSeen is never 0 when we have valid health data
              // This handles the case where game 1 completed (minHealthSeen=0) and game 2 starts
              if (minHealthSeenRef.current === 0 && initialHealthRef.current === 30) {
                minHealthSeenRef.current = 30;
                console.log(`📊 [Reconnect] Forced reset minHealthSeen from 0 to 30 (stale from previous game)`);
              }
              
              const playerHealth = gameState.state[playerBotId]?.health ?? 30;
              const opponentHealth = gameState.state[opponentBotId]?.health ?? 30;
              const currentMinHealth = Math.min(playerHealth, opponentHealth);
              
              console.log(`📊 [Reconnect] Health check:`, {
                playerHealth,
                opponentHealth,
                currentMinHealth,
                minHealthSeen: minHealthSeenRef.current,
                initialHealth: initialHealthRef.current,
                hasReconnected: hasReconnectedRef.current,
                isNewGame: isNewGame
              });
              
              // ✅ CRITICAL: Always update minHealthSeenRef if health is lower OR if we just reconnected OR if minHealthSeen is 0 or invalid (stale from previous game)
              // This ensures progress is calculated correctly even on first update after reconnect
              const shouldUpdate = currentMinHealth < minHealthSeenRef.current || 
                                   minHealthSeenRef.current === 30 || 
                                   hasReconnectedRef.current || 
                                   minHealthSeenRef.current === 0 ||
                                   minHealthSeenRef.current < 0;
              
              if (shouldUpdate) {
                minHealthSeenRef.current = currentMinHealth;
                
                // ✅ CRITICAL: Only set progress to 100% when game is actually complete (winner is set)
                // If health is <= 0 but winner is not set, cap progress at 99% (game is almost done but not complete)
                // Health can go negative, so we check <= 0, not === 0
                let progress = Math.min(100, Math.max(0, ((initialHealthRef.current - currentMinHealth) / initialHealthRef.current) * 100));
                if (currentMinHealth <= 0 && !gameState.winner) {
                  progress = 99; // Game is almost done but not complete yet (health is 0 or negative, but winner not set)
                  console.log(`⚠️ [Reconnect] Health is ${currentMinHealth} (<= 0) but winner not set yet - capping progress at 99%`);
                }
                
                setGameProgress(progress, `Reconnect health update (health=${currentMinHealth}, gameId=${data.gameId}, winner=${gameState.winner || 'none'})`);
                console.log(`📊 [Reconnect] Progress ${minHealthSeenRef.current === 30 ? 'initialized' : 'updated'}: health=${currentMinHealth}, progress=${progress}%, winner=${gameState.winner || 'none'}, reconnected=${hasReconnectedRef.current}`);
                hasReconnectedRef.current = false; // Reset flag after first update
              } else {
                console.log(`📊 [Reconnect] Progress not updated (health ${currentMinHealth} >= minHealthSeen ${minHealthSeenRef.current})`);
              }
              
              // ✅ CRITICAL: Check for game end - ONLY set progress to 100% when winner is actually set
              if (gameState.winner) {
                console.log(`🏆 [Reconnect] ✅ GAME COMPLETED: ${data.gameId} at ${new Date().toISOString()}, winner: ${gameState.winner}`);
                setGameProgress(100, `Reconnect game completed (winner: ${gameState.winner}, gameId: ${data.gameId})`);
                const playerBotIdCheck = playerBotIdRef.current;
                if (playerBotIdCheck && gameState.winner === playerBotIdCheck) {
                  setGameResult('win');
                } else if (playerBotIdCheck) {
                  setGameResult('loss');
                }
                
                // ✅ CRITICAL: Don't try to reconnect here - let processGameQueue handle it
                // The queue system will automatically detect game 2 and reconnect
                // This prevents duplicate reconnection attempts and race conditions
              }
            }
          }
        }
      } catch (error) {
        console.error('❌ Error parsing WebSocket message:', error);
      }
    };

    socket.onerror = (error) => {
      console.error('❌ [Reconnect] WebSocket error during reconnect:', error);
      reconnectingRef.current = false; // Clear reconnecting flag
      reconnectTargetGameIdRef.current = null; // Clear target
      try { socket.close(); } catch {}
      setWs(null);
      localStorage.setItem('autobattler_has_active_ws', 'false');
    };

    socket.onclose = (event) => {
      console.log(`🔄 [Reconnect] WebSocket closed (code: ${event.code}, reason: ${event.reason || 'none'})`);
      reconnectingRef.current = false; // Clear reconnecting flag
      reconnectTargetGameIdRef.current = null; // Clear target
      // Clear ws state if this socket was set as the active WebSocket
      setWs(prevWs => {
        if (prevWs === socket) {
          localStorage.setItem('autobattler_has_active_ws', 'false');
          return null;
        }
        return prevWs;
      });
    };
  };

  // Keep WebSocket reference to prevent it from being closed
  const wsRef = useRef(null);
  
  useEffect(() => {
    wsRef.current = ws;
  }, [ws]);
  
  useEffect(() => {
    return () => {
      // Only close WebSocket on component unmount if we don't have a started game
      // This cleanup runs when component unmounts, not when ws changes
      if (wsRef.current && !startedGameIdRef.current) {
        console.log("🔌 Closing WebSocket on unmount (no started game)");
        try {
          wsRef.current.close();
        } catch (e) {
          console.warn("Error closing WebSocket:", e);
        }
      } else if (wsRef.current && startedGameIdRef.current) {
        console.log("🔌 Keeping WebSocket open for game:", startedGameIdRef.current);
      }
    };
  }, []); // Empty deps - only run on unmount



  // Start a single autobattler game
  const startSingleGame = (strategyToUse, factionName, token, gameIndex, totalGames, aggressive = false) => {
    return new Promise((resolve, reject) => {
      const socket = new window.WebSocket(WS_URL);
      let gameCompleted = false;
      let gameId = null;
      let promiseResolved = false; // Track if promise has been resolved to prevent double resolution
      
      // Helper function to refresh resources and history after game completion
      const refreshAfterGameEnd = async () => {
        try {
          const currentToken = await ensureValidToken();
          await fetch(`${API_URL}/unviewed-autobattler-rewards`, {
            headers: { Authorization: `Bearer ${currentToken}` }
          });
          fetchGameHistory();
          
          // Refresh resources after battle completes
          const resourcesResponse = await fetch(`${API_URL}/user-resources`, {
            headers: { Authorization: `Bearer ${currentToken}` }
          });
          if (resourcesResponse.ok) {
            const resourcesData = await resourcesResponse.json();
            setResources(resourcesData);
          }
          
          // Refresh reserved resources
          const reservedResponse = await fetch(`${API_URL}/reserved-autobattler-resources`, {
            headers: { Authorization: `Bearer ${currentToken}` }
          });
          if (reservedResponse.ok) {
            const reservedData = await reservedResponse.json();
            setReservedResources(reservedData);
          }
        } catch (err) {
          console.error('Failed to refresh after game end:', err);
          fetchGameHistory();
        }
      };
      
      // Helper function to resolve promise and clean up
      const resolveGame = async () => {
        if (promiseResolved) {
          console.log(`⚠️ Attempted to resolve game ${gameId} twice, ignoring second call`);
          return;
        }
        promiseResolved = true;
        
        // Close socket first
        try { 
          socket.close(); 
        } catch (e) {
          console.warn('Error closing socket:', e);
        }
        
        // ✅ CRITICAL: Clear ws state when game completes
        // Check if this socket is still the active one before clearing
        hasActiveGameRef.current = false; // Mark as no active game
        
        // ✅ FIX: Clear startedGameId if this was the active game
        if (startedGameIdRef.current === gameId) {
          console.log(`🔄 [Game Complete] Clearing startedGameId for completed game ${gameId}`);
          setStartedGameId(null);
          startedGameIdRef.current = null;
        }
        
        setWs(prevWs => {
          if (prevWs === socket) {
            console.log(`🔄 [Game Complete] Clearing WebSocket for completed game ${gameId}`);
            localStorage.setItem('autobattler_has_active_ws', 'false');
            return null; // Clear ws
          }
          return prevWs; // Keep other socket if it exists
        });
        
        // Resolve promise immediately
        resolve({ gameId, completed: true });
        
        // Do async cleanup in background (don't block promise resolution)
        setTimeout(refreshAfterGameEnd, 2000);
      };

      socket.onopen = () => {
        console.log(`🔌 [Game ${gameIndex + 1}] WebSocket opened, sending auth...`);
        const currentLanguage = localStorage.getItem('i18nextLng') || 'en-us';
        const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
        socket.send(JSON.stringify(getWebSocketAuthMessage(token, normalizedLanguage)));
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log(`📨 [Game ${gameIndex + 1}] WebSocket message:`, data.type, { 
            gameId: data.gameId, 
            hasGameState: !!data.gameState,
            currentStartedGameId: startedGameIdRef.current,
            matches: data.gameId === startedGameIdRef.current
          });

          if (data.type === 'auth-success') {
            console.log(`✅ [Game ${gameIndex + 1}] Auth successful, sending autobattler-start...`);
            socket.send(
              JSON.stringify({
                type: 'autobattler-start',
                strategyId: parseInt(strategyToUse),
                factionName: factionName,
                aggressive: !!aggressive
              })
            );
            return;
          }

          if (data.type === 'match-found') {
            console.log(`📨 [Game ${gameIndex + 1}] Received match-found for Dispatch Asset:`, data);
            gameId = data.gameId;
            
            // Store gameId in ref FIRST (before state update) so cleanup effect can check it
            const newGameId = data.gameId;
            startedGameIdRef.current = newGameId;
            hasActiveGameRef.current = true; // Mark as active game
            // Then update state using functional update to ensure React sees the change
            console.log(`🎮 [Game ${gameIndex + 1}] ✅ GAME STARTED: ${newGameId} at ${new Date().toISOString()}`);
            console.log(`📨 [Game ${gameIndex + 1}] Setting startedGameId to:`, newGameId);
            setStartedGameId(prev => {
              console.log(`📨 [Game ${gameIndex + 1}] setStartedGameId called - prev:`, prev, `new:`, newGameId);
              return newGameId;
            });
            
            // ✅ CRITICAL: Store playerBotId immediately if available
            if (data.gameState && data.gameState.playerBotId) {
              playerBotIdRef.current = data.gameState.playerBotId;
            }
            
            // Initialize progress tracking
            if (data.gameState) {
              const gameState = data.gameState;
              const playerBotId = gameState.playerBotId;
              const opponentBotId = gameState.opponentBotId;
              
              // Store playerBotId to check winner later
              playerBotIdRef.current = playerBotId;
              
              if (playerBotId && opponentBotId && gameState.state) {
                const playerHealth = gameState.state[playerBotId]?.health ?? 30;
                const opponentHealth = gameState.state[opponentBotId]?.health ?? 30;
                // ✅ CRITICAL: For autobattler games, initial health is always 30
                // Don't use Math.max because both bots start at 30
                const initialHealthValue = 30;
                initialHealthRef.current = initialHealthValue;
                // Set minHealthSeenRef to current minimum health (represents progress so far)
                const currentMinHealth = Math.min(playerHealth, opponentHealth);
                minHealthSeenRef.current = currentMinHealth;
                
                // ✅ CRITICAL: Calculate initial progress if game has already started
                if (currentMinHealth < 30) {
                  // ✅ CRITICAL: Only set progress to 100% when game is actually complete (winner is set)
                  // If health is <= 0 but winner is not set, cap progress at 99% (game is almost done but not complete)
                  // Health can go negative, so we check <= 0, not === 0
                  let progress = Math.min(100, Math.max(0, ((30 - currentMinHealth) / 30) * 100));
                  if (currentMinHealth <= 0 && !gameState.winner) {
                    progress = 99; // Game is almost done but not complete yet (health is 0 or negative, but winner not set)
                    console.log(`⚠️ [Game ${gameIndex + 1}] Health is ${currentMinHealth} (<= 0) but winner not set yet - capping progress at 99%`);
                  }
                  setGameProgress(progress, `Progress initialized for game ${gameIndex + 1} (health=${currentMinHealth}, winner=${gameState.winner || 'none'})`);
                  console.log(`📊 Progress initialized for game ${gameIndex + 1}: health=${currentMinHealth}, progress=${progress}%, winner=${gameState.winner || 'none'}`);
                } else {
                  setGameProgress(0, 'Mount: No active queue and no local queue info');
                  console.log("📊 Progress initialized for new game:", { initialHealth: initialHealthValue, playerHealth, opponentHealth });
                }
              }
            }
            
            clearDiscordPresence();
            console.log(`✅ [Game ${gameIndex + 1}] WebSocket kept open for progress updates, gameId:`, data.gameId);
            // ✅ CRITICAL: Store socket in state for ALL games so UI can track it
            setWs(socket);
            localStorage.setItem('autobattler_has_active_ws', 'true');
            return;
          }
      
          // ✅ CRITICAL: Update queue progress from WebSocket message (source of truth)
          // This can override the initial total set from POST response if there are old queue items
          // ✅ FIX: Check for queueProgress.total (not current) because current can be 0
          // ✅ WAIT: We wait for WebSocket to send queueProgress - no API fallbacks
          // ✅ DEBUG: Log ALL messages to see if queueProgress is included
          if (data.queueProgress) {
            console.log(`📋 [WebSocket] Message has queueProgress:`, {
              type: data.type,
              current: data.queueProgress.current,
              total: data.queueProgress.total,
              currentType: typeof data.queueProgress.current,
              hasTotal: !!data.queueProgress.total
            });
          }
          
          if (data.queueProgress && typeof data.queueProgress.current === 'number' && data.queueProgress.total) {
            const previousTotal = queueProgress.total;
            const previousCurrent = queueProgress.current;
            console.log(`📋 [WebSocket] ✅ Received queue progress: ${data.queueProgress.current}/${data.queueProgress.total} (was: ${previousCurrent}/${previousTotal})`);
            if (previousTotal > 0 && previousTotal !== data.queueProgress.total) {
              console.log(`⚠️ [WebSocket] WebSocket total (${data.queueProgress.total}) differs from initial total (${previousTotal}), using WebSocket value (handles old queue items)`);
            }
            setQueueProgress({
              current: data.queueProgress.current,
              total: data.queueProgress.total,
              currentFaction: queueProgress.currentFaction || ''
            });
          } else if (data.queueProgress) {
            // queueProgress exists but doesn't meet our conditions
            console.warn(`⚠️ [WebSocket] queueProgress exists but invalid:`, {
              current: data.queueProgress.current,
              currentType: typeof data.queueProgress.current,
              total: data.queueProgress.total,
              hasTotal: !!data.queueProgress.total
            });
          } else if (queueProgress.total > 0 && queueProgress.current === 0) {
            // Log that we're waiting for WebSocket to send queueProgress
            console.log(`⏳ [WebSocket] Waiting for WebSocket to send queueProgress (message type: ${data.type}, current: ${queueProgress.current}, total: ${queueProgress.total})`);
          }
          
          // Handle game state updates for progress tracking
          if ((data.type === 'game-update' || data.type === 'turn-played' || data.type === 'autobattler-started') && data.gameState) {
            const currentGameId = startedGameIdRef.current;
            
            // ✅ CRITICAL: If we have a gameId in the message, use it (it's the source of truth)
            // This fixes the issue where match-found has one gameId but turn-played has another
            if (data.gameId && data.gameId.includes('_autobattler_')) {
              // Always update gameId from turn-played/game-update messages (they're the source of truth)
              if (!currentGameId || currentGameId !== data.gameId) {
                const isNewGame = currentGameId && currentGameId !== data.gameId;
                
                // ✅ CRITICAL: If current game completed AND we're in a queue AND this is a different gameId,
                // this message is for the NEXT game - ignore it (processGameQueue will handle it)
                if (gameCompleted && queueRef.current.processing && isNewGame) {
                  console.log(`⏭️ [Game ${gameIndex + 1}] Ignoring message for next game (gameId: ${data.gameId}) - current game completed, queue will handle next game`);
                  return; // Ignore this message - it's for the next game
                }
                
                console.log(`📊 [Game ${gameIndex + 1}] Updating gameId from ${data.type}: ${data.gameId} (was: ${currentGameId})`);
                
                // ✅ CRITICAL: If this is a NEW game (gameId changed), reset health refs
                if (isNewGame) {
                  console.log(`📊 [Game ${gameIndex + 1}] NEW GAME DETECTED! Resetting health refs (old gameId: ${currentGameId}, new gameId: ${data.gameId})`);
                  minHealthSeenRef.current = 30;
                  initialHealthRef.current = 30;
                  setGameProgress(0, `Game ${gameIndex + 1}: New game detected (gameId changed)`);
                }
                
                startedGameIdRef.current = data.gameId;
                setStartedGameId(data.gameId);
                gameId = data.gameId; // Also update local gameId variable
              }
            }
            
            // ✅ CRITICAL: Process ALL autobattler game updates in this WebSocket
            // Don't require exact gameId match - if it's an autobattler game in this socket, process it
            // This handles the case where match-found has wrong gameId but turn-played has correct one
            const shouldProcess = (data.gameId && data.gameId.includes('_autobattler_')) || 
                                 (data.gameState && data.gameState.isAutobattler) ||
                                 (!data.gameId && currentGameId && currentGameId.includes('_autobattler_'));
            
            // ✅ CRITICAL: If current game completed AND we're in a queue, check if this message is for a different game
            // If it has a different gameId, ignore it (it's for the next game which processGameQueue will handle)
            if (gameCompleted && queueRef.current.processing && shouldProcess) {
              const messageGameId = data.gameId || (data.gameState?.gameId);
              const isDifferentGame = messageGameId && currentGameId && messageGameId !== currentGameId;
              if (isDifferentGame) {
                console.log(`⏭️ [Game ${gameIndex + 1}] Ignoring ${data.type} message for next game (gameId: ${messageGameId}, current: ${currentGameId}) - current game completed, queue will handle next game`);
                return; // Ignore this message - it's for the next game
              }
              // If message doesn't have a gameId or it matches current game, we can still process it
              // (might be final state updates for the completed game)
            }
            
            if (shouldProcess) {
              const gameState = data.gameState;
              const playerBotId = gameState.playerBotId;
              const opponentBotId = gameState.opponentBotId;
              
              // ✅ CRITICAL: Ensure startedGameId is set for game 2 (or any game)
              // This ensures progress bar shows even if match-found didn't set it
              if (data.gameId && data.gameId.includes('_autobattler_') && !startedGameIdRef.current) {
                console.log(`📊 [Game ${gameIndex + 1}] Setting startedGameId from ${data.type} (was not set): ${data.gameId}`);
                startedGameIdRef.current = data.gameId;
                setStartedGameId(data.gameId);
              }
              
              // ✅ CRITICAL: Check if this is a NEW game (different gameId) - if so, reset health refs
              // Note: Use startedGameIdRef.current here (may have been updated above)
              const currentGameIdAfterUpdate = startedGameIdRef.current;
              const isNewGameAfterUpdate = currentGameIdAfterUpdate && data.gameId && currentGameIdAfterUpdate !== data.gameId;
              
              // ✅ CRITICAL: If current game completed AND we're in a queue AND this is a different gameId,
              // this message is for the NEXT game - ignore it (processGameQueue will handle it)
              // (This is a second check in case the first one at line 1665 didn't catch it)
              if (gameCompleted && queueRef.current.processing && isNewGameAfterUpdate) {
                console.log(`⏭️ [Game ${gameIndex + 1}] Ignoring ${data.type} message for next game (gameId: ${data.gameId}, current: ${currentGameIdAfterUpdate}) - current game completed, queue will handle next game`);
                return; // Ignore this message - it's for the next game
              }
              
              // For logging, use the updated value
              const isNewGame = isNewGameAfterUpdate;
              
              console.log(`📊 [Game ${gameIndex + 1}] Processing ${data.type} for gameId: ${data.gameId}`, {
                playerBotId,
                opponentBotId,
                hasState: !!gameState.state,
                currentGameId: startedGameIdRef.current,
                isNewGame: isNewGame,
                gameCompleted: gameCompleted,
                queueProcessing: queueRef.current.processing
              });
              
              if (playerBotId && opponentBotId && gameState.state) {
                // ✅ CRITICAL: Store playerBotId if not set
                if (!playerBotIdRef.current) {
                  playerBotIdRef.current = playerBotId;
                }
                
                // ✅ CRITICAL: If this is a NEW game OR minHealthSeen is 0 (stale from previous game), reset health refs
                if (isNewGame || minHealthSeenRef.current === 0 || minHealthSeenRef.current < 0) {
                  console.log(`📊 [Game ${gameIndex + 1}] Resetting health refs (isNewGame=${isNewGame}, minHealthSeen=${minHealthSeenRef.current})`);
                  minHealthSeenRef.current = 30;
                  initialHealthRef.current = 30;
                  setGameProgress(0, `Game ${gameIndex + 1}: New game detected or stale health refs`);
                } else if (!initialHealthRef.current) {
                  initialHealthRef.current = 30;
                }
                
                const playerHealth = gameState.state[playerBotId]?.health ?? 30;
                const opponentHealth = gameState.state[opponentBotId]?.health ?? 30;
                const currentMinHealth = Math.min(playerHealth, opponentHealth);
                
                console.log(`📊 [Game ${gameIndex + 1}] Health check:`, {
                  playerHealth,
                  opponentHealth,
                  currentMinHealth,
                  minHealthSeen: minHealthSeenRef.current,
                  initialHealth: initialHealthRef.current,
                  isNewGame: isNewGame,
                  hasWinner: !!gameState.winner,
                  winner: gameState.winner || 'none'
                });
                
                // ✅ CRITICAL: Always update minHealthSeenRef if health is lower OR if minHealthSeen is 0 or 30 (initial state)
                // This ensures progress is calculated correctly even on first update
                if (currentMinHealth < minHealthSeenRef.current || minHealthSeenRef.current === 30 || minHealthSeenRef.current === 0) {
                  minHealthSeenRef.current = currentMinHealth;
                  
                  // ✅ CRITICAL: Only set progress to 100% when game is actually complete (winner is set)
                  // If health is <= 0 but winner is not set, cap progress at 99% (game is almost done but not complete)
                  // Health can go negative, so we check <= 0, not === 0
                  let progress = Math.min(100, Math.max(0, ((initialHealthRef.current - currentMinHealth) / initialHealthRef.current) * 100));
                  if (currentMinHealth <= 0 && !gameState.winner) {
                    progress = 99; // Game is almost done but not complete yet (health is 0 or negative, but winner not set)
                    console.log(`⚠️ [Game ${gameIndex + 1}] Health is ${currentMinHealth} (<= 0) but winner not set yet - capping progress at 99%`);
                  }
                  
                  setGameProgress(progress, `Game ${gameIndex + 1} health update (health=${currentMinHealth}, winner=${gameState.winner || 'none'})`);
                  console.log(`📊 [Game ${gameIndex + 1}] Progress ${minHealthSeenRef.current === 30 ? 'initialized' : 'updated'}: health=${currentMinHealth}, progress=${progress}%, winner=${gameState.winner || 'none'}`);
                } else {
                  console.log(`📊 [Game ${gameIndex + 1}] Progress not updated (health ${currentMinHealth} >= minHealthSeen ${minHealthSeenRef.current})`);
                }
                
                // ✅ CRITICAL: Check for game end - ONLY set progress to 100% when winner is actually set
                if (gameState.winner && !gameCompleted) {
                  gameCompleted = true;
                  console.log(`🏆 [Game ${gameIndex + 1}] ✅ GAME COMPLETED: ${data.gameId} at ${new Date().toISOString()}, winner: ${gameState.winner}`);
                  setGameProgress(100, `Game ${gameIndex + 1} completed (winner: ${gameState.winner})`);
                  const playerBotIdCheck = playerBotIdRef.current;
                  if (playerBotIdCheck && gameState.winner === playerBotIdCheck) {
                    setGameResult('win');
                  } else if (playerBotIdCheck) {
                    setGameResult('loss');
                  }
                  
                  // ✅ CRITICAL: Don't clear progress immediately - let the queue system handle transitions
                  // The queue will automatically start game 2, and we'll reconnect to it
                  // Only clear if there's truly no more games (handled by processGameQueue)
                  
                  // Wait for game to be stored in database, then refresh history
                  // Use retry logic to ensure history is updated
                  const refreshHistoryWithRetry = async (retries = 3, delay = 2000) => {
                    for (let i = 0; i < retries; i++) {
                      try {
                        const currentToken = await ensureValidToken();
                        
                        // First, check if game is in history
                        const historyResponse = await fetch(`${API_URL}/autobattler-history`, {
                          headers: { Authorization: `Bearer ${currentToken}` }
                        });
                        if (historyResponse.ok) {
                          const historyData = await historyResponse.json();
                          const gameInHistory = historyData.history?.some(h => h.gameId === gameId);
                          
                          if (gameInHistory || i === retries - 1) {
                            // Game found in history or last retry - refresh UI
                            fetchGameHistory();
                            break;
                          } else {
                            // Game not found yet, wait and retry
                            console.log(`⏳ Game ${gameId} not in history yet, retrying in ${delay}ms (attempt ${i + 1}/${retries})`);
                            await new Promise(resolve => setTimeout(resolve, delay));
                            continue;
                          }
                        }
                      } catch (err) {
                        console.error(`Failed to check history (attempt ${i + 1}/${retries}):`, err);
                        if (i === retries - 1) {
                          // Last retry failed, just refresh anyway
                          fetchGameHistory();
                        } else {
                          await new Promise(resolve => setTimeout(resolve, delay));
                        }
                      }
                    }
                  };
                  
                  // Schedule async refresh operations and promise resolution
                  // Use a delay to allow game state to be stored in database
                  setTimeout(() => {
                    if (!promiseResolved) {
                      resolveGame();
                    }
                    // Do async refresh in background (don't block promise resolution)
                    refreshHistoryWithRetry().catch(err => {
                      console.error('Failed to refresh history:', err);
                      fetchGameHistory();
                    });
                    refreshAfterGameEnd().catch(err => {
                      console.error('Failed to refresh after game end:', err);
                    });
                  }, 2000);
                }
              }
            }
            return;
          }
          
          if (data.type === 'error') {
            console.error('❌ Dispatch Asset error:', data.message);
            if (!promiseResolved) {
              promiseResolved = true;
              try { socket.close(); } catch {}
              setWs(null);
              reject(new Error(data.message || 'Failed to start game'));
            }
            return;
          }
        } catch (error) {
          console.error('❌ Error parsing WebSocket message:', error);
        }
      };

      socket.onerror = (error) => {
        console.error('WebSocket error:', error);
        if (!promiseResolved) {
          promiseResolved = true;
          try { socket.close(); } catch {}
          setWs(null);
          reject(error);
        }
      };

      socket.onclose = () => {
        console.log(`🔌 WebSocket closed for game ${gameIndex + 1}, gameCompleted=${gameCompleted}, queueProcessing=${queueRef.current.processing}, promiseResolved=${promiseResolved}`);
        // ✅ CRITICAL: Clear ws state if this socket was the active one
        // Only clear if game completed OR if queue is not processing (no more games)
        setWs(prevWs => {
          if (prevWs === socket) {
            if (gameCompleted || !queueRef.current.processing) {
              console.log(`🔄 [Socket Close] Clearing WebSocket - game completed or queue finished`);
              hasActiveGameRef.current = false; // Mark as no active game
              localStorage.setItem('autobattler_has_active_ws', 'false');
              return null; // Clear ws
            } else {
              // Queue still processing, but this socket closed - clear flag but keep ws for next game
              hasActiveGameRef.current = false; // Temporarily no active game until next one starts
              localStorage.setItem('autobattler_has_active_ws', 'false');
              return prevWs; // Keep ws for next game in queue
            }
          }
          return prevWs; // Different socket, keep it
        });

        // ✅ FIX: If game completed, resolve immediately (don't wait for setTimeout)
        // This fixes the race condition where socket closes after gameCompleted=true but before setTimeout runs
        if (gameCompleted && !promiseResolved) {
          console.log(`✅ Game ${gameIndex + 1} completed, resolving promise immediately on socket close`);
          resolveGame();
          return;
        }
        
        // Only reject if game didn't complete and we're NOT processing a queue
        // If we're processing a queue, resolve instead of reject to allow queue to continue
        if (!gameCompleted && !promiseResolved) {
          if (queueRef.current.processing) {
            console.warn(`⚠️ WebSocket closed for game ${gameIndex + 1} but queue is still processing - resolving to continue queue`);
            if (!promiseResolved) {
              promiseResolved = true;
              // Resolve instead of reject - let the queue continue
              // The game might have completed on the server even if the frontend lost connection
              resolve({ gameId, completed: false, warning: 'Connection closed but game may have completed' });
            }
          } else {
            // Not in a queue, so this is a real error
            if (!promiseResolved) {
              promiseResolved = true;
              reject(new Error('Connection closed unexpectedly'));
            }
          }
        } else if (promiseResolved) {
          console.log(`ℹ️ WebSocket closed for game ${gameIndex + 1} but promise already resolved, ignoring`);
        }
      };
      
      // ✅ CRITICAL: Store socket in state for ALL games so UI can track it
      // This ensures React knows about the WebSocket and can update the UI
      // Store it immediately when socket is created, not just in match-found
      setWs(socket);
      localStorage.setItem('autobattler_has_active_ws', 'true');
      console.log(`🔌 [Game ${gameIndex + 1}] WebSocket stored in state`);
    });
  };

  // Process queue of games sequentially
  // ✅ CRITICAL: Submit queue to server first, then process locally for UI updates
  // Server continues processing even if client disconnects
  const processGameQueue = async (factionConfigsToProcess) => {
    console.log(`🚀 [Queue] processGameQueue CALLED with:`, {
      configs: factionConfigsToProcess,
      alreadyProcessing: queueRef.current.processing,
      wsExists: !!ws,
      wsReadyState: ws?.readyState
    });
    
    // ✅ CRITICAL: Check if we're actually processing or just stuck
    if (queueRef.current.processing) {
      // Verify we actually have an active connection
      const hasActiveWs = localStorage.getItem('autobattler_has_active_ws') === 'true';
      if (!ws || ws.readyState !== WebSocket.OPEN || !hasActiveWs) {
        // We're marked as processing but not actually connected - clear the flag
        console.log(`🧹 [Queue] Stuck processing flag detected (no active WS), clearing it`);
        queueRef.current.processing = false;
        queueCompletedRef.current = false; // Reset completion flag
        setQueueProcessing(false);
      } else {
        console.log(`⚠️ [Queue] Already processing with active connection, returning early`);
        return;
      }
    }

    // Filter to only process factions with valid strategies and count > 0
    const validConfigs = factionConfigsToProcess.filter(config => 
      config.strategyId && config.count > 0 && 
      (!strategyValidations[config.strategyId] || strategyValidations[config.strategyId]?.valid !== false)
    );

    console.log(`🔍 [Queue] Valid configs:`, validConfigs);

    if (validConfigs.length === 0) {
      console.log(`❌ [Queue] No valid configs found`);
      showNotification(t('battleOverview.noValidFactions'), 'warning', 6000);
      return;
    }

    // Calculate total games
    const totalGames = validConfigs.reduce((sum, config) => sum + config.count, 0);
    console.log(`📊 [Queue] Total games to process: ${totalGames}`);

    queueRef.current.processing = true;
    queueCompletedRef.current = false; // Reset completion flag when starting new queue
    setQueueProcessing(true);
    // ✅ CRITICAL: Don't set queue progress here - WebSocket messages are the source of truth
    // Queue progress will ONLY be set when WebSocket messages arrive with queueProgress data
    // No local "made up" data - server is the single source of truth
    console.log(`✅ [Queue] Queue processing started, totalGames: ${totalGames}`);
    
    updateDiscordPresence('In Queue', null, true);

    // Ensure token is valid
    let token;
    try {
      token = await ensureValidToken();
    } catch (error) {
      console.error('Token validation failed:', error);
      showNotification(error.message || t('battleOverview.tokenRefreshFailed'), 'error', 6000);
      queueRef.current.processing = false;
      queueCompletedRef.current = false; // Reset completion flag
      setQueueProcessing(false);
      return;
    }
    
    // ✅ CRITICAL: Submit queue to server first (so it continues if client disconnects)
    try {
      console.log(`📋 Submitting queue to server: ${totalGames} games`);
      const response = await fetch(`${API_URL}/autobattler-queue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          factionConfigs: validConfigs.map(config => ({
            strategyId: config.strategyId,
            factionName: config.factionName,
            count: config.count,
            aggressive: !!config.aggressive
          }))
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to submit queue');
      }
      
      const data = await response.json();
      console.log(`✅ Queue submitted successfully: ${data.queueItemsCount} games`);
      
      // Resources are reserved only when queue is submitted. Refresh so available updates immediately.
      try {
        const resourcesResponse = await fetch(`${API_URL}/user-resources`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (resourcesResponse.ok) {
          const resourcesData = await resourcesResponse.json();
          setResources(resourcesData);
        }
        const reservedResponse = await fetch(`${API_URL}/reserved-autobattler-resources`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (reservedResponse.ok) {
          const reservedData = await reservedResponse.json();
          setReservedResources(reservedData);
        }
      } catch (refreshErr) {
        console.error('Failed to refresh resources after queue submit:', refreshErr);
      }
      
      // ✅ OPTION 1: Set queueProgress.total immediately from backend response
      // This shows "Battle 0 of 2" immediately instead of "No assets dispatched"
      // WebSocket will update current when games start, and can override total if different (handles old queue items)
      if (data.queueItemsCount && data.queueItemsCount > 0) {
        setQueueProgress(prev => ({
          current: 0,  // Will be updated by WebSocket when first game starts
          total: data.queueItemsCount,  // Total games in this submission
          currentFaction: prev.currentFaction || ''  // Preserve faction if set
        }));
        console.log(`📋 [Queue] Set initial queueProgress.total to ${data.queueItemsCount} from POST response`);
      }
    } catch (error) {
      console.error('Error submitting queue to server:', error);
      showNotification(t('battleOverview.failedToSubmitQueue'), 'error', 6000);
      queueRef.current.processing = false;
      queueCompletedRef.current = false;
      setQueueProcessing(false);
      return;
    }
    
    // ✅ CRITICAL: Frontend should NOT start games - backend handles all game creation
    // Frontend only needs to:
    // 1. Poll for active games that backend has started
    // 2. Reconnect to them when found
    // 3. Wait for queue to complete
    
    console.log(`📋 [Queue] Queue submitted to backend. Backend will start games automatically.`);
    console.log(`📋 [Queue] Frontend will poll for active games and reconnect when found.`);
    
    // Poll for active games and reconnect to them
    // The polling mechanism in useEffect will handle reconnecting to games
    // We just need to wait for the queue to complete
    let lastGameId = null;
    const maxWait = 600000; // 10 minutes max for entire queue
    const startTime = Date.now();
    
    while (queueRef.current.processing && (Date.now() - startTime) < maxWait && !navigatingToSpectateRef.current) {
      await new Promise(resolve => setTimeout(resolve, 3000)); // Poll every 3 seconds
      
      // ✅ FIX: Check if user is navigating to spectate - stop all activity
      if (navigatingToSpectateRef.current) {
        console.log(`🛑 [Queue] Stopping queue polling - user navigating to spectate`);
        break;
      }
      
      try {
        // Check queue status
        const queueStatusResponse = await fetch(`${API_URL}/autobattler-queue-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (queueStatusResponse.ok) {
          const queueStatus = await queueStatusResponse.json();
          
          // Check if queue is complete
          if (queueStatus.pending === 0 && queueStatus.processing === 0) {
            console.log(`✅ [Queue] All games completed. Queue finished.`);
            break;
          }
          
          // ✅ FIX: Double-check navigation flag before reconnecting
          if (navigatingToSpectateRef.current) {
            console.log(`🛑 [Queue] Stopping before reconnect - user navigating to spectate`);
            break;
          }
          
          // Check for active game
          const currentGameResponse = await fetch(`${API_URL}/autobattler-current-game`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (currentGameResponse.ok) {
            const currentGameData = await currentGameResponse.json();
            if (currentGameData.gameId && currentGameData.gameId !== lastGameId) {
              // New game started - reconnect to it
              console.log(`🔄 [Queue] Backend started new game: ${currentGameData.gameId}`);
              lastGameId = currentGameData.gameId;
              
              // Reconnect to the game if not already connected
              if (!(ws && ws.readyState === WebSocket.OPEN && startedGameIdRef.current === currentGameData.gameId)) {
                reconnectToActiveGame(currentGameData.gameId, token);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error polling queue status:', error);
      }
    }
    
    // All games completed
    if (queueRef.current.processing) {
      // ✅ CRITICAL: Mark queue as completed BEFORE clearing state
      // This prevents polling from reconnecting to new games after queue completes
      queueCompletedRef.current = true;
      
      // ✅ CRITICAL: Clear queue progress when queue completes - WebSocket messages are the source of truth
      // If backend sends a completion message with queueProgress, it will update from there
      setQueueProgress({ current: 0, total: 0, currentFaction: '' });
      // ✅ CRITICAL: Clear game progress and startedGameId when all games complete
      setGameProgress(0, 'Mount: No active queue');
      setStartedGameId(null);
      startedGameIdRef.current = null;
      hasActiveGameRef.current = false; // Mark as no active game
      setHasOngoingBattles(false); // Clear ongoing battles state
      setWs(null);
      clearDiscordPresence();
    }
    
    queueRef.current.processing = false;
    setQueueProcessing(false);

    // ✅ CRITICAL: Keep queue info in localStorage for display, but mark as completed
    // Don't remove it - we want to show "Battle X/Y" even after completion
    // ✅ CRITICAL: ALWAYS clear WebSocket when all games complete - this unblocks the UI
    console.log(`🔄 [Queue Complete] Clearing WebSocket - all ${totalGames} games completed`);
    hasActiveGameRef.current = false; // Mark as no active game
    setWs(prevWs => {
      if (prevWs) {
        try {
          prevWs.close();
        } catch {}
        localStorage.setItem('autobattler_has_active_ws', 'false');
      }
      return null; // Always return null to clear ws
    });
    
    // Refresh rewards, history, and resources
    setTimeout(async () => {
      try {
        const token = await ensureValidToken();
        const response = await fetch(`${API_URL}/unviewed-autobattler-rewards`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          // Include all wins (even if rewards are empty due to daily limit)
          // Backend already filters for wins only, so we just need to ensure it's an array
          const validRewards = (data.unviewedRewards || []).filter(reward => {
            return reward.outcome === 'win';
          });
          
          // Only update state if rewards actually changed (prevents unnecessary re-renders)
          setUnviewedRewards(prev => {
            if (rewardsEqual(prev, validRewards)) {
              return prev; // Return previous array reference if unchanged
            }
            return validRewards;
          });
          setUnviewedRewardsCount(validRewards.length);
        }
        
        // ✅ FIX: Refresh resources after queue completion
        const resourcesResponse = await fetch(`${API_URL}/user-resources`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (resourcesResponse.ok) {
          const resourcesData = await resourcesResponse.json();
          setResources(resourcesData);
        }
        
        // Also refresh reserved resources
        const reservedResponse = await fetch(`${API_URL}/reserved-autobattler-resources`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (reservedResponse.ok) {
          const reservedData = await reservedResponse.json();
          setReservedResources(reservedData);
        }
        
        fetchGameHistory();
      } catch (err) {
        console.error('Failed to refresh after queue completion:', err);
      }
    }, 3000);
  };

  const handleAutobattlerClick = async () => {
    setLastQueueFailure(null); // Clear previous failure when starting a new run
    console.log(`🖱️ [Button] Dispatch Asset button clicked`);
    console.log(`🔍 [Button] Current state:`, {
      wsExists: !!ws,
      wsReadyState: ws?.readyState,
      hasActiveGame: hasActiveGameRef.current,
      queueProcessing: queueProcessing,
      queueProgress: queueProgress,
      factionConfigs: factionConfigs
    });
    
    // ✅ CRITICAL: Force clear stale state if WebSocket is closed
    if (ws && ws.readyState !== WebSocket.OPEN) {
      console.log(`🧹 [Button] Clearing stale WebSocket (readyState: ${ws.readyState})`);
      try {
        ws.close();
      } catch {}
      setWs(null);
      localStorage.setItem('autobattler_has_active_ws', 'false');
      hasActiveGameRef.current = false;
      setStartedGameId(null);
      startedGameIdRef.current = null;
            setGameProgress(0, 'Mount: No active queue');
    }
    
    // ✅ CRITICAL: Check server state - if no active queue, clear local state
    let hasOngoingBattles = false;
    try {
      const token = await ensureValidToken();
      const queueStatusResponse = await fetch(`${API_URL}/autobattler-queue-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (queueStatusResponse.ok) {
        const queueStatus = await queueStatusResponse.json();
        const active = parseInt(queueStatus.active || 0);
        console.log(`🔍 [Button] Server queue status:`, { active, queueStatus });
        
        // Check if there are ongoing battles (pending or processing)
        if (active > 0) {
          hasOngoingBattles = true;
          setHasOngoingBattles(true); // Update state
          console.log(`⚠️ [Button] Ongoing battles detected (${active} active). Cannot start new dispatch missions.`);
        } else {
          setHasOngoingBattles(false); // Update state
        }
        
        if (active === 0) {
          // No active queue on server - force clear local state
          console.log(`🧹 [Button] No active queue on server, clearing local state`);
          setWs(null);
          localStorage.setItem('autobattler_has_active_ws', 'false');
          hasActiveGameRef.current = false;
          setStartedGameId(null);
          startedGameIdRef.current = null;
            setGameProgress(0, 'Mount: No active queue');
          setQueueProcessing(false);
          queueRef.current.processing = false;
          queueCompletedRef.current = false; // Reset completion flag
          setHasOngoingBattles(false); // Clear ongoing battles state
          setHasOngoingBattles(false); // Clear ongoing battles state
        } else {
          // There's an active queue on server - check if we're actually connected
          const hasActiveWs = localStorage.getItem('autobattler_has_active_ws') === 'true';
          if (!ws || ws.readyState !== WebSocket.OPEN || !hasActiveWs) {
            // Server has queue but we're not connected - this is a stuck state
            // Clear processing flag so we can reconnect
            console.log(`🧹 [Button] Server has active queue (${active}) but we're not connected - clearing stuck processing flag`);
            queueRef.current.processing = false;
            queueCompletedRef.current = false; // Reset completion flag
            setQueueProcessing(false);
            // Try to reconnect to the active game
            const currentGameResponse = await fetch(`${API_URL}/autobattler-current-game`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (currentGameResponse.ok) {
              const currentGameData = await currentGameResponse.json();
              if (currentGameData.gameId) {
                console.log(`🔄 [Button] Reconnecting to active game: ${currentGameData.gameId}`);
                reconnectToActiveGame(currentGameData.gameId, token);
                return; // Don't start new queue, reconnect to existing
              }
            }
          }
        }
      }
    } catch (err) {
      console.error(`❌ [Button] Error checking queue status:`, err);
      // Continue anyway - don't block if check fails
    }
    
    // ✅ CRITICAL: Block if there are ongoing battles
    if (hasOngoingBattles) {
      showNotification(t('battleOverview.ongoingBattlesBlockNew'), 'warning', 6000);
      return;
    }
    
    // ✅ CRITICAL: Only block if we have an ACTIVE WebSocket connection
    // Don't block based on queueProcessing/queueProgress - those might be stale
    // The server will handle queue management
    if (ws && ws.readyState === WebSocket.OPEN && hasActiveGameRef.current) {
      console.log(`⚠️ [Button] Blocked - Active WebSocket exists (readyState: ${ws.readyState}, hasActiveGame: ${hasActiveGameRef.current})`);
      return;
    }
    
    console.log(`✅ [Button] Proceeding with battle start...`);
    
    // Validate all selected factions have valid strategies
    const invalidConfigs = factionConfigs.filter(config => 
      config.count > 0 && (!config.strategyId || (strategyValidations[config.strategyId] && !strategyValidations[config.strategyId]?.valid))
    );

    if (invalidConfigs.length > 0) {
      const invalidFactions = invalidConfigs.map(c => {
        const translatedName = factionTranslations[c.factionName] || c.factionName;
        return translatedName;
      }).join(', ');
      
      const firstInvalid = invalidConfigs[0];
      const validation = strategyValidations[firstInvalid.strategyId];
      if (validation && validation.insufficientOrders && validation.insufficientOrders.length > 0) {
        const orderList = validation.insufficientOrders.map(o => `${o.name} (need ${o.required}, have ${o.owned})`).join(', ');
        showNotification(`${t('battleOverview.cannotStartDispatchAsset')} ${orderList}`, 'warning', 6000);
      } else {
        showNotification(`${t('battleOverview.cannotStartDispatchAssetNoOrders')} ${invalidFactions}`, 'warning', 6000);
      }
      return;
    }

    // Check if at least one faction has games to process
    const hasValidConfigs = factionConfigs.some(config => 
      config.strategyId && config.count > 0 && 
      (!strategyValidations[config.strategyId] || strategyValidations[config.strategyId]?.valid !== false)
    );

    if (!hasValidConfigs) {
      showNotification(t('battleOverview.noValidFactionsMessage'), 'warning', 6000);
      return;
    }

    // Check if user has enough resources (uses raw resources; see hasEnoughResources)
    const totalCosts = calculateTotalResourceCosts();
    if (!hasEnoughResources(totalCosts)) {
      const missing = [];
      if (resources.lavender < totalCosts.lavender) missing.push(`${totalCosts.lavender - resources.lavender} ${t('battleOverview.lavender')}`);
      if (resources.biomass < totalCosts.biomass) missing.push(`${totalCosts.biomass - resources.biomass} ${t('battleOverview.biomass')}`);
      if (resources.foodRations < totalCosts.foodRations) missing.push(`${totalCosts.foodRations - resources.foodRations} ${t('battleOverview.foodRations')}`);
      if (resources.stone < totalCosts.stone) missing.push(`${totalCosts.stone - resources.stone} ${t('battleOverview.stone')}`);
      if (resources.erray < totalCosts.erray) missing.push(`${totalCosts.erray - resources.erray} ${t('battleOverview.erray')}`);
      showNotification(`${t('battleOverview.insufficientResources')} ${missing.join(', ')}`, 'warning', 6000);
      return;
    }

    // Start queue processing
    await processGameQueue(factionConfigs);
  };

  const handleCancelQueue = async () => {
    queueRef.current.processing = false;
    queueCompletedRef.current = false; // Reset completion flag
    setQueueProcessing(false);
    setQueueProgress({ current: 0, total: 0, currentFaction: '' });

    // ✅ CRITICAL: Queue progress is managed via WebSocket messages only, no localStorage needed
    // Note: We keep faction configs in localStorage so resource costs remain visible
    
    if (ws) {
      try {
        ws.close();
      } catch {}
      setWs(null);
    }
    
    // ✅ CRITICAL: Clear startedGameId and game progress when canceling
    setStartedGameId(null);
    startedGameIdRef.current = null;
            setGameProgress(0, 'Mount: No active queue');
    setGameResult(null);
    
    clearDiscordPresence();
  };

  const handleSpectateClick = () => {
    if (!startedGameId) return;
    
    playSoundEffect('/sounds/click.mp3');
    
    // ✅ CRITICAL FIX #1: Set navigation flag FIRST to stop all queue polling and reconnects
    // This flag is checked in the queue polling loop and reconnect functions
    navigatingToSpectateRef.current = true;
    console.log(`🚀 [Spectate] Navigation flag set - stopping all DispatchAsset WebSocket activity`);
    
    // ✅ CRITICAL FIX #2: Close ALL WebSockets BEFORE navigating to BattleUI
    // This prevents race condition where two WebSockets compete for the same game
    // Close both the state-tracked ws AND the ref-tracked ws (they might be different due to async state updates)
    
    // Close ws state
    if (ws) {
      console.log(`🔌 [Spectate] Closing DispatchAsset ws state WebSocket`);
      try {
        ws.close();
      } catch (e) {
        console.warn('Error closing ws state WebSocket:', e);
      }
      setWs(null);
    }
    
    // Close wsRef (might be different socket due to async React state updates)
    if (wsRef.current && wsRef.current !== ws) {
      console.log(`🔌 [Spectate] Closing DispatchAsset wsRef WebSocket (different from ws state)`);
      try {
        wsRef.current.close();
      } catch (e) {
        console.warn('Error closing wsRef WebSocket:', e);
      }
    }
    wsRef.current = null;
    
    // ✅ Clear localStorage flag so BattleUI knows there's no competing connection
    localStorage.removeItem('autobattler_has_active_ws');
    
    // Find the strategy ID from the current game's faction (if available)
    // For simplicity, we'll use the first valid strategy from configs
    const firstValidConfig = factionConfigs.find(config => config.strategyId);
    const strategyId = firstValidConfig?.strategyId || '';
    
    const navigationState = {
      strategyId: strategyId,
      userId: localStorage.getItem('userId'),
      token: localStorage.getItem('token'),
      selectedGeneral: selectedGeneral,
      selectedAdvisor: selectedAdvisor,
      isBotGame: false, // Not a bot game, it's Dispatch Asset
      yourTurn: false // Player is spectator, never their turn
    };
    
    console.log(`🚀 [Spectate] Navigating to BattleUI for game: ${startedGameId}`);
    navigate(`/battle/${startedGameId}`, {
      state: navigationState
    });
  };

  // Helper to get strategies for a specific faction
  const getStrategiesForFaction = (factionName) => {
    if (!factionName) return allStrategies;
    return allStrategies.filter(strategy => strategy.faction_name === factionName);
  };

  // Keep carousel index in bounds when the list of factions with strategies changes
  useEffect(() => {
    const visibleCount = factionConfigs.filter(
      config => getStrategiesForFaction(config.factionName).length > 0
    ).length;
    if (visibleCount > 0 && carouselIndex >= visibleCount) {
      setCarouselIndex(visibleCount - 1);
    }
  }, [factionConfigs, allStrategies]);

  // Restore carousel selection from localStorage once on mount (remember last viewed faction)
  useEffect(() => {
    if (factionConfigs.length === 0 || allStrategies.length === 0 || hasRestoredCarouselRef.current) return;
    const visibleConfigs = factionConfigs.filter(
      config => getStrategiesForFaction(config.factionName).length > 0
    );
    if (visibleConfigs.length === 0) return;
    try {
      const savedFaction = localStorage.getItem('autobattler_carousel_faction');
      if (savedFaction) {
        const idx = visibleConfigs.findIndex(c => c.factionName === savedFaction);
        if (idx >= 0) {
          setCarouselIndex(idx);
        }
      }
    } catch (_) {}
    hasRestoredCarouselRef.current = true;
  }, [factionConfigs, allStrategies]);

  // Persist carousel selection so it survives refresh and navigation
  useEffect(() => {
    if (factionConfigs.length === 0 || allStrategies.length === 0) return;
    const visibleConfigs = factionConfigs.filter(
      config => getStrategiesForFaction(config.factionName).length > 0
    );
    const safeIndex = Math.min(carouselIndex, Math.max(0, visibleConfigs.length - 1));
    const factionName = visibleConfigs[safeIndex]?.factionName;
    if (factionName) {
      try {
        localStorage.setItem('autobattler_carousel_faction', factionName);
      } catch (_) {}
    }
  }, [carouselIndex, factionConfigs, allStrategies]);

  // Preselect first available strategy per faction when none is selected
  useEffect(() => {
    if (factionConfigs.length === 0 || allStrategies.length === 0) return;
    factionConfigs.forEach(config => {
      const strategies = getStrategiesForFaction(config.factionName);
      if (strategies.length > 0 && !config.strategyId) {
        updateFactionConfig(config.factionName, 'strategyId', strategies[0].id.toString());
      }
    });
  }, [factionConfigs, allStrategies]);

  // Calculate resource costs for a faction
  const calculateFactionResourceCost = (factionName, count) => {
    const costResources = factionResourceCosts[factionName] || [];
    const costs = {
      lavender: 0,
      biomass: 0,
      foodRations: 0,
      stone: 0,
      erray: 0
    };
    
    costResources.forEach(resource => {
      costs[resource] = count * RESOURCE_COST_PER_BATTLE;
    });
    
    return costs;
  };

  // Calculate total battles across all factions
  const calculateTotalBattles = () => {
    return factionConfigs.reduce((total, config) => total + (config.count || 0), 0);
  };

  // Calculate total resource costs across all factions
  const calculateTotalResourceCosts = () => {
    const totalCosts = {
      lavender: 0,
      biomass: 0,
      foodRations: 0,
      stone: 0,
      erray: 0
    };
    
    factionConfigs.forEach(config => {
      if (config.count > 0) {
        const factionCosts = calculateFactionResourceCost(config.factionName, config.count);
        Object.keys(totalCosts).forEach(key => {
          totalCosts[key] += factionCosts[key];
        });
      }
    });
    
    return totalCosts;
  };

  // Check if user has enough resources to cover the planned dispatch.
  // Use raw resources: reservedResources is the same as the planned cost, so we must not
  // subtract it when checking sufficiency (that would wrongly require 2x the cost).
  const hasEnoughResources = (costs) => {
    return Object.keys(costs).every(key => resources[key] >= costs[key]);
  };

  // Why is the Dispatch button disabled? (so user sees the reason without guessing)
  const getDispatchDisabledReason = () => {
    const noConfig = factionConfigs.every(c => !c.strategyId || c.count === 0);
    if (noConfig) {
      return { disabled: true, reason: t('battleOverview.dispatchDisabledNoConfig'), canClear: false };
    }
    const totalCosts = calculateTotalResourceCosts();
    if (!hasEnoughResources(totalCosts)) {
      const missing = [];
      Object.keys(totalCosts).forEach(k => { if (totalCosts[k] > 0 && resources[k] < totalCosts[k]) missing.push(`${totalCosts[k] - resources[k]} ${k}`); });
      return { disabled: true, reason: t('battleOverview.dispatchDisabledNoResources') + ' ' + missing.join(', '), canClear: false };
    }
    const wsStuck = ws && ws.readyState === WebSocket.OPEN && hasActiveGameRef.current;
    if (wsStuck) {
      return { disabled: true, reason: t('battleOverview.dispatchDisabledWsStuck'), canClear: true };
    }
    if (hasOngoingBattles) {
      return { disabled: true, reason: t('battleOverview.dispatchDisabledOngoing'), canClear: true };
    }
    return { disabled: false, reason: '', canClear: false };
  };

  // Clear stuck queue/ws state and re-sync with server (so button can enable again)
  const handleClearDispatchState = async () => {
    if (ws) {
      try { ws.close(); } catch (_) {}
      setWs(null);
    }
    localStorage.setItem('autobattler_has_active_ws', 'false');
    hasActiveGameRef.current = false;
    setStartedGameId(null);
    startedGameIdRef.current = null;
    setQueueProcessing(false);
    queueRef.current.processing = false;
    queueCompletedRef.current = false;
    setHasOngoingBattles(false);
    setGameProgress(0, 'Clear state');
    setQueueProgress({ current: 0, total: 0, currentFaction: '' });
    try {
      const token = await ensureValidToken();
      const res = await fetch(`${API_URL}/autobattler-queue-status`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        const active = parseInt(data.active || 0);
        setHasOngoingBattles(active > 0);
        setLastQueueFailure(data.lastFailure || null);
      }
    } catch (_) {}
  };

  // Update faction config only. Do NOT reserve or update available resources when planning.
  // Resources are only reserved/deducted when the player clicks Dispatch (server creates reservations on queue submit).
  const updateFactionConfig = async (factionName, field, value) => {
    setFactionConfigs(prev => prev.map(config =>
      config.factionName === factionName
        ? { ...config, [field]: value }
        : config
    ));
  };

  const handleMarkRewardsAsViewed = async () => {
    // Mark all displayed rewards as viewed
    for (const rewardData of unviewedRewards) {
      try {
        const token = await ensureValidToken();
        await fetch(`${API_URL}/battle-rewards/${rewardData.gameId}/view`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      } catch (err) {
        console.error(`Failed to mark rewards as viewed for game ${rewardData.gameId}:`, err);
      }
    }
    
    // Refresh unviewed rewards count
    try {
      const token = await ensureValidToken();
      const response = await fetch(`${API_URL}/unviewed-autobattler-rewards`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Include all wins (even if rewards are empty due to daily limit)
        // Backend already filters for wins only, so we just need to ensure it's an array
        const validRewards = (data.unviewedRewards || []).filter(reward => {
          return reward.outcome === 'win';
        });
        
        // Only update state if rewards actually changed (prevents unnecessary re-renders)
        setUnviewedRewards(prev => {
          if (rewardsEqual(prev, validRewards)) {
            return prev; // Return previous array reference if unchanged
          }
          return validRewards;
        });
        setUnviewedRewardsCount(validRewards.length);
      }
      
      // ✅ FIX: Refresh resources after rewards are claimed
      const resourcesResponse = await fetch(`${API_URL}/user-resources`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (resourcesResponse.ok) {
        const resourcesData = await resourcesResponse.json();
        setResources(resourcesData);
      }
      
      // Also refresh reserved resources
      const reservedResponse = await fetch(`${API_URL}/reserved-autobattler-resources`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (reservedResponse.ok) {
        const reservedData = await reservedResponse.json();
        setReservedResources(reservedData);
      }
    } catch (err) {
      console.error('Failed to refresh unviewed rewards:', err);
    }
  };

  return (
    <div className="dispatch-asset-viewport-wrapper">
      {/* Full-screen background - outside viewport-container to cover entire screen */}
      <div 
        className="dispatch-asset-fullscreen-background"
      />
      
      {/* Viewport container - 1920x1080 space that scales to fit screen */}
      <div 
        className={`dispatch-asset-viewport-container${isContainerMobile ? ' mobile' : ''}${isContainerLargeDesktop ? ' large-desktop' : ''}`}
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className="dispatch-asset-container">
          <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 1000, display: 'flex', alignItems: 'center' }}>
            <FriendsIcon />
            <SettingsMenu onLogout={() => {
              playSoundEffect('/sounds/click.mp3');
              onLogout();
              navigate('/');
            }} />
            <span style={{ color: 'white', marginRight: '10px' }}>{typeof user === 'string' ? user : (user?.username ?? '')}</span>
          </div>

          <button onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            navigate('/battleoverview');
          }} className="back-button">
            {t('common.back')}
          </button>


          {/* Spectate Return Toast Notification */}
          {spectateReturnMessage && (
            <div 
              className="spectate-return-toast"
              onClick={() => setSpectateReturnMessage(null)}
              style={{
                position: 'absolute',
                top: '80px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: spectateReturnMessage.includes('won') ? 'rgba(34, 139, 34, 0.95)' : 'rgba(139, 0, 0, 0.95)',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '18px',
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                zIndex: 1100,
                cursor: 'pointer',
                animation: 'fadeInDown 0.3s ease-out',
                border: '2px solid rgba(255, 255, 255, 0.3)'
              }}
            >
              {spectateReturnMessage}
            </div>
          )}

          {/* In-game notification (insufficient resources, validation errors, etc.) */}
          {NotificationComponent}

          {/* Main Content */}
          <div className="dispatch-asset-content">
            {/* Tabs */}
            <div className="dispatch-asset-tabs">
              <button
                className={`dispatch-asset-tab ${activeTab === 'setup' ? 'active' : ''}`}
                onClick={() => {
                  playSoundEffect('/sounds/click.mp3');
                  setActiveTab('setup');
                }}
              >
                {t('battleOverview.setup')}
              </button>
              <button
                className={`dispatch-asset-tab ${activeTab === 'history' ? 'active' : ''}`}
                onClick={() => {
                  playSoundEffect('/sounds/click.mp3');
                  setActiveTab('history');
                  // Refresh history when switching to History tab
                  fetchGameHistory(true);
                }}
              >
                {t('battleOverview.history')}
              </button>
              <button
                className={`dispatch-asset-tab ${activeTab === 'rewards' ? 'active' : ''}`}
                onClick={() => {
                  playSoundEffect('/sounds/click.mp3');
                  setActiveTab('rewards');
                }}
              >
                {t('battleOverview.claimRewards')} {unviewedRewardsCount > 0 && `(${unviewedRewardsCount})`}
              </button>
            </div>

            {/* Setup Tab */}
            {activeTab === 'setup' && (
              <div className="dispatch-asset-tab-content">
                {/* Battle Progress and Resources at Top - Always shown to prevent UI jumping */}
                <div className="current-game-status">
                  <div className="battle-progress-display">
                    {/* Resources on the left */}
                    <div className="resources-list">
                      {(() => {
                        const totalCosts = calculateTotalResourceCosts();
                        // Available = current resources minus only already-reserved (from pending battles). Not reduced when planning.
                        const available = {
                          lavender: resources.lavender - reservedResources.lavender,
                          biomass: resources.biomass - reservedResources.biomass,
                          foodRations: resources.foodRations - reservedResources.foodRations,
                          stone: resources.stone - reservedResources.stone,
                          erray: resources.erray - reservedResources.erray
                        };
                        // Insufficient = can't afford planned dispatch (compare raw resources to total cost)
                        const insufficient = (key) => resources[key] < totalCosts[key];
                        return (
                          <>
                            <div className="resource-item">
                              <div className="resource-icon-orb" style={{ backgroundColor: resourceOrbColors.lavender }}>
                                <img src="/images/resource_lavender.png" alt="Lavender" className="resource-icon" />
                              </div>
                              <span className="resource-name">{t('battleOverview.lavender')}</span>
                              <span className={`resource-amount ${insufficient('lavender') ? 'resource-insufficient' : ''}`}>
                                {available.lavender}
                              </span>
                            </div>
                            <div className="resource-item">
                              <div className="resource-icon-orb" style={{ backgroundColor: resourceOrbColors.biomass }}>
                                <img src="/images/resource_biomass.png" alt="Biomass" className="resource-icon" />
                              </div>
                              <span className="resource-name">{t('battleOverview.biomass')}</span>
                              <span className={`resource-amount ${insufficient('biomass') ? 'resource-insufficient' : ''}`}>
                                {available.biomass}
                              </span>
                            </div>
                            <div className="resource-item">
                              <div className="resource-icon-orb" style={{ backgroundColor: resourceOrbColors.foodRations }}>
                                <img src="/images/resource_foodrations.png" alt="Food Rations" className="resource-icon" />
                              </div>
                              <span className="resource-name">{t('battleOverview.foodRations')}</span>
                              <span className={`resource-amount ${insufficient('foodRations') ? 'resource-insufficient' : ''}`}>
                                {available.foodRations}
                              </span>
                            </div>
                            <div className="resource-item">
                              <div className="resource-icon-orb" style={{ backgroundColor: resourceOrbColors.stone }}>
                                <img src="/images/resource_stone.png" alt="Stone" className="resource-icon" />
                              </div>
                              <span className="resource-name">{t('battleOverview.stone')}</span>
                              <span className={`resource-amount ${insufficient('stone') ? 'resource-insufficient' : ''}`}>
                                {available.stone}
                              </span>
                            </div>
                            <div className="resource-item">
                              <div className="resource-icon-orb" style={{ backgroundColor: resourceOrbColors.erray }}>
                                <img src="/images/resource_erray.png" alt="Erray" className="resource-icon" />
                              </div>
                              <span className="resource-name">{t('battleOverview.erray')}</span>
                              <span className={`resource-amount ${insufficient('erray') ? 'resource-insufficient' : ''}`}>
                                {available.erray}
                              </span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    {/* Progress bar on the right */}
                    <div className="progress-bar-section">
                      {/* ✅ CRITICAL: Show queue progress when there's an active game/queue, otherwise show "No assets dispatched" */}
                      {(queueProgress.total > 0 && (startedGameId || (ws && ws.readyState === WebSocket.OPEN) || queueProcessing)) ? (
                        <span className="battle-progress-label">
                          {t('battleOverview.battle')} {queueProgress.current} {t('battleOverview.of')} {queueProgress.total}
                        </span>
                      ) : (
                        <span className="battle-progress-label">
                          {t('battleOverview.noAssetsDispatched')}
                        </span>
                      )}
                      <div className="progress-bar-container-inline">
                        {/* ✅ CRITICAL: Show progress bar if we have startedGameId OR if we have an active queue
                            This ensures progress shows for game 2 even if startedGameId isn't set yet */}
                        {(startedGameId || (queueProgress.total > 0 && (ws || queueProcessing))) ? (
                          <>
                            <div className="progress-bar-wrapper">
                              <div 
                                className="progress-bar-fill"
                                style={{ width: `${gameProgress}%` }}
                              />
                            </div>
                            <div className="progress-bar-text">
                              {Math.round(gameProgress)}%
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="progress-bar-wrapper">
                              <div 
                                className="progress-bar-fill"
                                style={{ width: '0%' }}
                              />
                            </div>
                            <div className="progress-bar-text">
                              0%
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Faction Carousel: cover-flow style, 5 visible items with center largest */}
                {(() => {
                  const visibleConfigs = factionConfigs.filter(config => {
                    const factionStrategies = getStrategiesForFaction(config.factionName);
                    return factionStrategies.length > 0;
                  });
                  const safeIndex = visibleConfigs.length === 0 ? 0 : Math.min(carouselIndex, visibleConfigs.length - 1);
                  const config = visibleConfigs[safeIndex];
                  if (visibleConfigs.length === 0) {
                    return (
                      <div className="dispatch-carousel-wrapper">
                        <div className="dispatch-carousel-placeholder">
                          {t('battleOverview.noFactionsWithStrategies')}
                        </div>
                      </div>
                    );
                  }
                  const factionStrategies = getStrategiesForFaction(config.factionName);
                  const n = visibleConfigs.length;
                  const levelClasses = ['dispatch-carousel-level1', 'dispatch-carousel-level0', 'dispatch-carousel-level-1'];
                  return (
                    <div className="dispatch-carousel-wrapper">
                      <div className="dispatch-carousel-coverflow">
                        <button
                          type="button"
                          className="dispatch-carousel-arrow dispatch-carousel-arrow-left"
                          onClick={() => {
                            playSoundEffect('/sounds/click.mp3');
                            setCarouselIndex(prev => (prev <= 0 ? n - 1 : prev - 1));
                          }}
                          disabled={n <= 1}
                          title={t('battleOverview.previousFaction')}
                          aria-label={t('battleOverview.previousFaction')}
                        >
                          ‹
                        </button>
                        <div className="dispatch-carousel-viewport">
                          {[0, 1, 2].map((slot) => {
                            const slotIndex = (safeIndex - 1 + slot + n) % n;
                            const cfg = visibleConfigs[slotIndex];
                            const name = factionTranslations[cfg.factionName] || cfg.factionName;
                            const src = getCarouselImage(cfg.factionName);
                            const isCenter = slot === 1;
                            const isThoth = src.includes('thoth_carousel');
                            const isBorran = src.includes('borran_carousel');
                            const heroName = getFactionHeroName(cfg.factionName);
                            return (
                              <div
                                key={n >= 3 ? cfg.factionName : `${slot}-${cfg.factionName}`}
                                className={`dispatch-carousel-item ${levelClasses[slot]}`}
                                onClick={() => {
                                  if (!isCenter) {
                                    playSoundEffect('/sounds/click.mp3');
                                    setCarouselIndex(slotIndex);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    if (!isCenter) {
                                      playSoundEffect('/sounds/click.mp3');
                                      setCarouselIndex(slotIndex);
                                    }
                                  }
                                }}
                                role="button"
                                tabIndex={0}
                                aria-label={name}
                                aria-current={isCenter ? 'true' : undefined}
                              >
                                <div className="dispatch-carousel-item-image-area">
                                  {isThoth ? (
                                    <div className="dispatch-carousel-item-image-wrap-thoth" aria-hidden="true">
                                      <img src={src} alt={name} className="dispatch-carousel-item-image dispatch-carousel-item-image--thoth" />
                                    </div>
                                  ) : isBorran ? (
                                    <div className="dispatch-carousel-item-image-wrap-borran" aria-hidden="true">
                                      <img src={src} alt={name} className="dispatch-carousel-item-image dispatch-carousel-item-image--borran" />
                                    </div>
                                  ) : (
                                    <img src={src} alt={name} className="dispatch-carousel-item-image" />
                                  )}
                                </div>
                                {heroName ? (
                                  <div className="dispatch-carousel-item-hero" aria-hidden="true">{heroName}</div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          className="dispatch-carousel-arrow dispatch-carousel-arrow-right"
                          onClick={() => {
                            playSoundEffect('/sounds/click.mp3');
                            setCarouselIndex(prev => (prev >= n - 1 ? 0 : prev + 1));
                          }}
                          disabled={n <= 1}
                          title={t('battleOverview.nextFaction')}
                          aria-label={t('battleOverview.nextFaction')}
                        >
                          ›
                        </button>
                      </div>
                      <div className="dispatch-catalog-and-actions">
                      <div className="dispatch-catalog-order">
                        <div className="dispatch-catalog-row">
                          <span className="dispatch-catalog-label dispatch-catalog-col-faction">{t('battleOverview.faction')}</span>
                          <span className="dispatch-catalog-label dispatch-catalog-col-strategy">{t('battleOverview.strategy')}</span>
                          <span className="dispatch-catalog-label dispatch-catalog-col-battles">{t('battleOverview.queueCount')}</span>
                          <span className="dispatch-catalog-label dispatch-catalog-col-price">{t('battleOverview.totalPrice')}</span>
                          <div className="dispatch-catalog-value dispatch-catalog-col-faction">
                            <span className="dispatch-catalog-value-text">{factionTranslations[config.factionName] || config.factionName}</span>
                          </div>
                          <div className="dispatch-catalog-value dispatch-catalog-col-strategy">
                            <CustomDropdown
                              options={factionStrategies.map(s => ({
                                value: s.id.toString(),
                                label: s.name,
                                faction_name: s.faction_name,
                                hasWarning: strategyValidations[s.id]?.valid === false
                              }))}
                              value={config.strategyId || ''}
                              onChange={(strategyId) => {
                                playSoundEffect('/sounds/click.mp3');
                                updateFactionConfig(config.factionName, 'strategyId', strategyId || '');
                              }}
                              placeholder={t('battleOverview.selectStrategyDropdown')}
                              renderOption={(option) => {
                                const strategy = factionStrategies.find(s => s.id.toString() === option.value);
                                const factionName = strategy?.faction_name || option.faction_name;
                                const validation = strategyValidations[option.value];
                                const isValid = validation?.valid !== false;
                                if (!factionName) {
                                  return <span>{option.label} {!isValid ? '⚠️' : ''}</span>;
                                }
                                const translated = factionTranslations[factionName] || factionName;
                                return (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <div
                                      className="faction-orb"
                                      style={{ background: getFactionOrbBackground(factionName) }}
                                      title={translated}
                                    />
                                    <span>{option.label} {!isValid ? '⚠️' : ''}</span>
                                  </div>
                                );
                            }}
                          />
                          </div>
                          <div className="dispatch-catalog-value dispatch-catalog-col-battles">
                            <div className="dispatch-catalog-battles-edit">
                              <button
                                type="button"
                                className="faction-config-counter-button"
                                onClick={async () => {
                                  playSoundEffect('/sounds/click.mp3');
                                  const newValue = Math.max(0, config.count - 1);
                                  await updateFactionConfig(config.factionName, 'count', newValue);
                                }}
                                disabled={(!!ws && hasActiveGameRef.current) || !config.strategyId || config.count <= 0}
                                title={t('battleOverview.decrease')}
                              >
                                −
                              </button>
                              <input
                                type="number"
                                min={0}
                                max={Math.max(0, 10 - (calculateTotalBattles() - (config.count || 0)))}
                                value={config.count || 0}
                                className="dispatch-catalog-battles-input"
                                disabled={(!!ws && hasActiveGameRef.current) || !config.strategyId}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (raw === '') return;
                                  const v = parseInt(raw, 10);
                                  if (isNaN(v)) return;
                                  const otherTotal = calculateTotalBattles() - (config.count || 0);
                                  const maxAllowed = Math.max(0, 10 - otherTotal);
                                  const clamped = Math.min(maxAllowed, Math.max(0, v));
                                  updateFactionConfig(config.factionName, 'count', clamped);
                                }}
                              />
                              <button
                                type="button"
                                className="faction-config-counter-button"
                                onClick={async () => {
                                  playSoundEffect('/sounds/click.mp3');
                                  const currentTotal = calculateTotalBattles();
                                  if (currentTotal >= 10) {
                                    alert(t('battleOverview.maxBattlesReached'));
                                    return;
                                  }
                                  // Affordability: compare raw resources to total cost if we add one more battle (no reservation until Dispatch)
                                  const currentCosts = calculateTotalResourceCosts();
                                  const additionalCosts = calculateFactionResourceCost(config.factionName, 1);
                                  const totalIfAddOne = {
                                    lavender: currentCosts.lavender + additionalCosts.lavender,
                                    biomass: currentCosts.biomass + additionalCosts.biomass,
                                    foodRations: currentCosts.foodRations + additionalCosts.foodRations,
                                    stone: currentCosts.stone + additionalCosts.stone,
                                    erray: currentCosts.erray + additionalCosts.erray
                                  };
                                  if (resources.lavender < totalIfAddOne.lavender || resources.biomass < totalIfAddOne.biomass || resources.foodRations < totalIfAddOne.foodRations || resources.stone < totalIfAddOne.stone || resources.erray < totalIfAddOne.erray) {
                                    alert(t('battleOverview.insufficientResources'));
                                    return;
                                  }
                                  await updateFactionConfig(config.factionName, 'count', config.count + 1);
                                }}
                                disabled={(!!ws && hasActiveGameRef.current) || !config.strategyId || calculateTotalBattles() >= 10}
                                title={t('battleOverview.increase')}
                              >
                                +
                              </button>
                            </div>
                          </div>
                          <div className="dispatch-catalog-value dispatch-catalog-col-price">
                            <span className="dispatch-catalog-price-value">
                              {(() => {
                                const costs = calculateTotalResourceCosts();
                                const items = [];
                                if (costs.lavender > 0) items.push({ amount: costs.lavender, name: t('battleOverview.lavender'), image: '/images/resource_lavender.png' });
                                if (costs.biomass > 0) items.push({ amount: costs.biomass, name: t('battleOverview.biomass'), image: '/images/resource_biomass.png' });
                                if (costs.foodRations > 0) items.push({ amount: costs.foodRations, name: t('battleOverview.foodRations'), image: '/images/resource_foodrations.png' });
                                if (costs.stone > 0) items.push({ amount: costs.stone, name: t('battleOverview.stone'), image: '/images/resource_stone.png' });
                                if (costs.erray > 0) items.push({ amount: costs.erray, name: t('battleOverview.erray'), image: '/images/resource_erray.png' });
                                if (items.length === 0) return '—';
                                return items.map((item, i) => (
                                  <span key={item.name} className="dispatch-catalog-price-item">
                                    {i > 0 && ' '}
                                    <img src={item.image} alt={item.name} className="dispatch-catalog-price-icon" />
                                    {item.amount} {item.name}
                                  </span>
                                ));
                              })()}
                            </span>
                          </div>
                        </div>
                        <div className="dispatch-catalog-row dispatch-catalog-row-aggressive">
                          <label className="dispatch-aggressive-checkbox-label">
                            <input
                              type="checkbox"
                              className="dispatch-aggressive-checkbox"
                              checked={!!config.aggressive}
                              onChange={() => {
                                playSoundEffect('/sounds/click.mp3');
                                updateFactionConfig(config.factionName, 'aggressive', !config.aggressive);
                              }}
                              disabled={!!ws && hasActiveGameRef.current}
                            />
                            <span className="dispatch-aggressive-checkbox-text">{t('battleOverview.aggressiveMode')}</span>
                          </label>
                        </div>
                      </div>
                <div className="dispatch-button-container">
                  {!startedGameId ? (
                    <div className="dispatch-controls">
                      <button
                        type="button"
                        className="dispatch-asset-button"
                        onClick={() => {
                          playSoundEffect('/sounds/click.mp3');
                          handleAutobattlerClick();
                        }}
                        disabled={getDispatchDisabledReason().disabled}
                        title={queueProcessing && queueProgress.total > 0 ? `Processing battle ${queueProgress.current} of ${queueProgress.total}` : ''}
                      >
                        <span className="dispatch-asset-button-text">{t('battleOverview.dispatch')}</span>
                      </button>
                      <button
                        type="button"
                        className="dispatch-asset-button dispatch-reset-button"
                        onClick={() => {
                          playSoundEffect('/sounds/click.mp3');
                          setFactionConfigs(prev => prev.map(c => ({ ...c, count: 0 })));
                        }}
                        title={t('battleOverview.resetBattlesTitle')}
                      >
                        <span className="dispatch-asset-button-text">{t('battleOverview.resetBattles')}</span>
                      </button>
                      {queueProcessing && queueProgress.total > 0 && (
                        <div className="queue-status-display">
                          {t('battleOverview.processing')} {queueProgress.current} / {queueProgress.total}
                        </div>
                      )}
                    </div>
                  ) : null}
                  {startedGameId ? (
                    <button
                      type="button"
                      className="spectate-button"
                      onClick={handleSpectateClick}
                    >
                      <span className="spectate-button-text">{t('battleOverview.spectate')}</span>
                    </button>
                  ) : null}
                </div>
                      </div>
                    </div>
                  );
                })()}

              </div>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
              <div className="dispatch-asset-tab-content">
                <div className="history-games-list">
                  {(() => {
                    console.log('🔍 Rendering History Tab');
                    console.log('🔍 gameHistory:', gameHistory);
                    console.log('🔍 gameHistory type:', Array.isArray(gameHistory) ? 'Array' : typeof gameHistory);
                    console.log('🔍 gameHistory.length:', gameHistory?.length || 0);
                    console.log('🔍 Will show games?', gameHistory?.length > 0);
                    return null;
                  })()}
                  {Array.isArray(gameHistory) && gameHistory.length > 0 ? (
                    <>
                      <div className="history-games-header">
                        <h3>{t('battleOverview.pastGames')}</h3>
                      </div>
                      <div className="history-games-content">
                        {gameHistory.map((game, index) => {
                          // Extract timestamp from game ID (format: userId_autobattler_timestamp)
                          const gameIdParts = game.gameId.split('_autobattler_');
                          const gameIdDisplay = gameIdParts.length > 1 ? gameIdParts[1] : game.gameId;
                          
                          return (
                            <div key={game.gameId} className="history-game-item">
                              <div className="history-game-id">
                                {gameIdDisplay}
                              </div>
                              <div className="history-game-strategy">
                                {game.strategyName || '-'}
                              </div>
                              <div className={`history-game-outcome ${game.userOutcome || 'unknown'}`}>
                                {game.userOutcome === 'win' 
                                  ? t('battleOverview.victory')
                                  : game.userOutcome === 'loss'
                                  ? t('battleOverview.defeat')
                                  : t('battleOverview.unknown')}
                              </div>
                              <div className="history-game-date">
                                {game.endedAt ? new Date(game.endedAt).toLocaleDateString() : ''}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div className="no-history-message">
                      {t('battleOverview.noPastGames')}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Rewards Tab */}
            {activeTab === 'rewards' && (
              <div className="dispatch-asset-tab-content">
                <RewardsTabContent
                  unviewedRewards={unviewedRewards}
                  t={t}
                  API_URL={API_URL}
                  ensureValidToken={ensureValidToken}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {showTutorialDispatchAsset && createPortal(
        <TutorialMessageBox
          image="/images/tutorial_explain_4.png"
          text={t('dispatchAsset.tutorialText')}
          onContinue={async () => {
            playSoundEffect('/sounds/click.mp3');
            const token = localStorage.getItem('token');
            if (token) {
              await fetch(`${API_URL}/tutorial/mark-seen`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ messageKey: 'dispatch_asset' }),
              });
              setSeenTutorialMessages(prev => prev.includes('dispatch_asset') ? prev : [...prev, 'dispatch_asset']);
            }
            setShowTutorialDispatchAsset(false);
          }}
          voiceKey="dispatchAsset.tutorialText"
        />,
        document.body
      )}
    </div>
  );
}

// Rewards Tab Content Component
function RewardsTabContent({ unviewedRewards, t, API_URL, ensureValidToken }) {
  const [enrichedRewards, setEnrichedRewards] = useState({}); // gameId -> rewards array
  const [isLoading, setIsLoading] = useState(true); // Loading state - wait for enrichment
  const scrollContainerRef = useRef(null); // Ref for scroll container
  const previousRewardsRef = useRef([]); // Track previous rewards to avoid unnecessary re-enrichment
  
  // Preserve scroll position during re-renders
  // Use useLayoutEffect to restore scroll synchronously before paint
  const savedScrollRef = useRef(0);
  
  // Save scroll position continuously as user scrolls
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const handleScroll = () => {
      savedScrollRef.current = container.scrollTop;
    };
    
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);
  
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (container && savedScrollRef.current > 0) {
      // Restore scroll position synchronously before browser paints
      container.scrollTop = savedScrollRef.current;
    }
  }, [enrichedRewards, isLoading]);
  
  useEffect(() => {
    // Check if rewards actually changed to avoid unnecessary re-enrichment
    const rewardsChanged = JSON.stringify(previousRewardsRef.current) !== JSON.stringify(unviewedRewards.map(r => r.gameId));
    
    if (!rewardsChanged && Object.keys(enrichedRewards).length > 0) {
      // Rewards haven't changed, skip re-enrichment
      console.log('🎁 [RewardsTab] Rewards unchanged, skipping re-enrichment');
      return;
    }
    
    // Enrich rewards with order data
    const enrichRewards = async () => {
      // Save scroll position before loading
      const container = scrollContainerRef.current;
      if (container) {
        savedScrollRef.current = container.scrollTop;
      }
      
      setIsLoading(true);
      console.log('🎁 [RewardsTab] Enriching rewards, unviewedRewards:', unviewedRewards);
      const token = await ensureValidToken();
      const enriched = {};
      
      for (const rewardData of unviewedRewards) {
        // Skip API fetch for dev rewards (gameId starts with 'dev-')
        if (rewardData.gameId && rewardData.gameId.startsWith('dev-')) {
          console.log(`🎁 [RewardsTab] Using dev rewards directly for game ${rewardData.gameId}`);
          enriched[rewardData.gameId] = rewardData.rewards || [];
          continue;
        }
        
        // Always try to fetch enriched rewards from the API for each game
        try {
          // Re-enrich rewards to ensure complete data
          const response = await fetch(`${API_URL}/battle-rewards/${rewardData.gameId}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            enriched[rewardData.gameId] = data.rewards || [];
            console.log(`🎁 [RewardsTab] Enriched rewards for game ${rewardData.gameId}:`, enriched[rewardData.gameId]);
          } else {
            // Fall back to rewards from initial fetch
            enriched[rewardData.gameId] = rewardData.rewards || [];
            console.log(`🎁 [RewardsTab] Using fallback rewards for game ${rewardData.gameId}:`, enriched[rewardData.gameId]);
          }
        } catch (err) {
          console.error(`Failed to enrich rewards for game ${rewardData.gameId}:`, err);
          enriched[rewardData.gameId] = rewardData.rewards || [];
        }
      }
      
      console.log('🎁 [RewardsTab] Final enriched rewards:', enriched);
      setEnrichedRewards(enriched);
      setIsLoading(false);
      
      // Update previous rewards ref
      previousRewardsRef.current = unviewedRewards.map(r => r.gameId);
      
      // Scroll position will be restored by useLayoutEffect
    };
    
    if (unviewedRewards.length > 0) {
      enrichRewards();
    } else {
      console.log('🎁 [RewardsTab] No unviewed rewards to enrich');
      setIsLoading(false);
      previousRewardsRef.current = [];
    }
  }, [unviewedRewards, API_URL, ensureValidToken]);

  // Filter rewards to only show games with actual rewards (after enrichment)
  // Use enriched rewards if available, otherwise fall back to original rewards
  const rewardsToShow = isLoading ? [] : unviewedRewards.filter(rewardData => {
    const enriched = enrichedRewards[rewardData.gameId];
    const rewards = enriched || rewardData.rewards || [];
    const hasRewards = rewardData.outcome === 'win' && Array.isArray(rewards) && rewards.length > 0;
    console.log(`🎁 [RewardsTab] Game ${rewardData.gameId}: outcome=${rewardData.outcome}, rewards.length=${rewards.length}, hasRewards=${hasRewards}`);
    return hasRewards;
  });
  
  console.log('🎁 [RewardsTab] rewardsToShow:', rewardsToShow.length, rewardsToShow);

  return (
    <div className="rewards-tab-content">
      <div className="rewards-tab-header">
        <h2>{t('battleOverview.unviewedRewards')}</h2>
      </div>
      
      <div className="rewards-tab-body" ref={scrollContainerRef}>
        {isLoading ? (
          <div className="rewards-loading">
            <p>{t('battleOverview.loadingRewards')}</p>
          </div>
        ) : rewardsToShow.length === 0 ? (
          <div className="no-rewards-message">
            {t('battleOverview.noUnviewedRewards')}
          </div>
        ) : (
          <div className="rewards-list">
            {rewardsToShow.map((rewardData, index) => {
              // Use enriched rewards if available, otherwise fall back to original rewards
              const rewards = enrichedRewards[rewardData.gameId] || rewardData.rewards || [];
              return (
                <div key={rewardData.gameId} className="reward-game-section">
                  <h3>
                    {t('battleOverview.game')} {index + 1} - {t('battleOverview.victory')}
                  </h3>
                  <div className="rewards-grid">
                    {rewards.map((reward, rewardIndex) => (
                      <RewardCard key={rewardIndex} reward={reward} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Rewards Modal Component
function RewardsModal({ unviewedRewards, onClose, t, API_URL, ensureValidToken }) {
  const [enrichedRewards, setEnrichedRewards] = useState({}); // gameId -> rewards array
  const [isLoading, setIsLoading] = useState(true); // Loading state - wait for enrichment
  
  useEffect(() => {
    // Enrich rewards with order data
    const enrichRewards = async () => {
      setIsLoading(true);
      const token = await ensureValidToken();
      const enriched = {};
      
      for (const rewardData of unviewedRewards) {
        // Skip API fetch for dev rewards (gameId starts with 'dev-')
        if (rewardData.gameId && rewardData.gameId.startsWith('dev-')) {
          console.log(`🎁 [RewardsModal] Using dev rewards directly for game ${rewardData.gameId}`);
          enriched[rewardData.gameId] = rewardData.rewards || [];
          continue;
        }
        
        // Always try to fetch enriched rewards from the API for each game
        try {
          // Re-enrich rewards to ensure complete data
          const response = await fetch(`${API_URL}/battle-rewards/${rewardData.gameId}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            enriched[rewardData.gameId] = data.rewards || [];
          } else {
            // Fall back to rewards from initial fetch
            enriched[rewardData.gameId] = rewardData.rewards || [];
          }
        } catch (err) {
          console.error(`Failed to enrich rewards for game ${rewardData.gameId}:`, err);
          enriched[rewardData.gameId] = rewardData.rewards || [];
        }
      }
      
      setEnrichedRewards(enriched);
      setIsLoading(false);
    };
    
    if (unviewedRewards.length > 0) {
      enrichRewards();
    } else {
      setIsLoading(false);
    }
  }, [unviewedRewards, API_URL, ensureValidToken]);

  // Filter rewards to only show games with actual rewards (after enrichment)
  const rewardsToShow = isLoading ? [] : unviewedRewards.filter(rewardData => {
    const rewards = enrichedRewards[rewardData.gameId] || [];
    return rewardData.outcome === 'win' && Array.isArray(rewards) && rewards.length > 0;
  });

  return (
    <div className="rewards-modal-overlay" onClick={onClose}>
      <div className="rewards-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="rewards-modal-header">
          <h2>{t('battleOverview.unviewedRewards')}</h2>
          <button className="rewards-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="rewards-modal-body">
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p>{t('battleOverview.loadingRewards')}</p>
            </div>
          ) : rewardsToShow.length === 0 ? (
            <p>{t('battleOverview.noUnviewedRewards')}</p>
          ) : (
            rewardsToShow.map((rewardData, index) => {
              const rewards = enrichedRewards[rewardData.gameId] || [];
              return (
                <div key={rewardData.gameId} className="reward-game-section">
                  <h3>
                    {t('battleOverview.game')} {index + 1} - {t('battleOverview.victory')}
                  </h3>
                  <div className="rewards-grid">
                    {rewards.map((reward, rewardIndex) => (
                      <RewardCard key={rewardIndex} reward={reward} />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
        
        <div className="rewards-modal-footer">
          <button className="rewards-modal-close-button" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Reward Card Component (matching AfterBattle reward display)
function RewardCard({ reward }) {
  if (!reward) return null;
  
  return (
    <div className="reward-card">
      {reward.type === 'order' && reward.data && reward.data.id && (
        <div className="reward-order-card">
          <div style={{ 
            width: '100%', 
            height: '100%', 
            position: 'relative',
            overflow: 'visible',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <div className={`reward-order-container rarity-${(reward.data.rarity || 'common').toLowerCase()}`} style={{
              width: '390px',
              height: '390px',
              position: 'relative',
              margin: 0,
              padding: 0
            }}>
              <OrderDesign
                order={reward.data}
                count={0}
                minimal={false}
                rarity={reward.data.rarity}
                lazyLoad={true}
                noMargin={true}
                gameState={null}
              />
              <OrderCardBubbles rarity={reward.data.rarity || 'common'} />
            </div>
          </div>
        </div>
      )}
      
      {reward.type === 'billy_bucks' && (
        <div style={{ textAlign: 'center', color: 'white', padding: '20px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <img 
            src="/images/billy_bucks.png" 
            alt="Billy Bucks" 
            style={{ width: '240px', height: '240px', marginBottom: '20px' }}
          />
          <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '10px' }}>
            {reward.amount || reward.data?.amount || 0}
          </div>
          <div style={{ fontSize: '1.2rem', color: '#ccc' }}>
            Billy Bucks
          </div>
        </div>
      )}
      
      {reward.type === 'resource' && (() => {
        // Map resource type to image path
        const getResourceImage = (resourceType) => {
          if (!resourceType) {
            console.warn('Resource reward missing resourceType:', reward);
            return '/images/resource_biomass.png';
          }
          
          const resourceMap = {
            'lavender': '/images/resource_lavender.png',
            'biomass': '/images/resource_biomass.png',
            'food_rations': '/images/resource_foodrations.png',
            'stone': '/images/resource_stone.png',
            'erray': '/images/resource_erray.png'
          };
          
          const imagePath = resourceMap[resourceType.toLowerCase()] || resourceMap[resourceType] || '/images/resource_biomass.png';
          console.log(`Resource reward: type=${resourceType}, image=${imagePath}`);
          return imagePath;
        };
        
        // Format resource name for display
        const getResourceName = (resourceType) => {
          if (!resourceType) return 'Resource';
          
          const nameMap = {
            'lavender': 'Lavender',
            'biomass': 'Biomass',
            'food_rations': 'Food Rations',
            'stone': 'Stone',
            'erray': 'Erray'
          };
          
          return nameMap[resourceType.toLowerCase()] || nameMap[resourceType] || resourceType;
        };
        
        // Get resource type - check multiple possible locations in reward object
        const resourceType = reward.resourceType || reward.data?.resourceType || reward.type;
        const amount = reward.amount || reward.data?.amount || 0;
        
        if (!resourceType || resourceType === 'resource') {
          console.warn('Resource reward missing valid resourceType:', reward);
        }
        
        return (
          <div style={{ textAlign: 'center', color: 'white', padding: '20px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <img 
              src={getResourceImage(resourceType)} 
              alt={getResourceName(resourceType)} 
              style={{ width: '240px', height: '240px', marginBottom: '20px', objectFit: 'contain' }}
              onError={(e) => {
                console.error(`Failed to load resource image for type: ${resourceType}`);
                // Fallback to biomass if image fails to load
                e.target.src = '/images/resource_biomass.png';
              }}
            />
            <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '10px' }}>
              {amount}
            </div>
            <div style={{ fontSize: '1.2rem', color: '#ccc' }}>
              {getResourceName(resourceType)}
            </div>
          </div>
        );
      })()}
      
      {reward.type === 'building' && (() => {
        const buildingId = reward.data?.building_id || '';
        const buildingDisplayNames = {
          powerplant: 'Powerplant',
          eclipse_array: 'Eclipse Array',
          eclipse_array_upgraded: 'Upgraded Eclipse Array',
          command_center: 'Command Center',
          cattle_ranch: 'Cattle Ranch',
          quarry: 'Quarry',
          sewers: 'Sewers',
          portal: 'Portal',
          lavender_field: 'Lavender Field',
          training_grounds: 'Training Grounds',
          barracks: 'Barracks',
          armory: 'Armory',
          drone_hangar: 'Drone Hangar',
          communications_hub: 'Communications Hub',
          intelligence_agency: 'Intelligence Agency',
          bank: 'Bank',
          research_center: 'Research Center',
          hospital: 'Hospital',
          hospital_upgraded: 'Upgraded Hospital',
          airfield: 'Airfield'
        };
        const displayName = buildingDisplayNames[buildingId] || buildingId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        const buildingImages = {
          powerplant: '/images/powerplant.png',
          eclipse_array: '/images/array.png',
          eclipse_array_upgraded: '/images/array_upgraded.png',
          command_center: '/images/command.png',
          cattle_ranch: '/images/cattle.png',
          quarry: '/images/stone.png',
          sewers: '/images/sewers.png',
          portal: '/images/erray.png',
          lavender_field: '/images/lavender.png',
          training_grounds: '/images/training.png',
          barracks: '/images/barracks.png',
          armory: '/images/armory.png',
          drone_hangar: '/images/dronehangar.png',
          communications_hub: '/images/comhub.png',
          intelligence_agency: '/images/intel.png',
          bank: '/images/bank.png',
          research_center: '/images/research.png',
          hospital: '/images/hospital.png',
          hospital_upgraded: '/images/hospital_upgraded.png',
          airfield: '/images/airfield.png'
        };
        const imgSrc = buildingImages[buildingId] || (buildingId ? `/images/${buildingId}.png` : '/images/command.png');
        return (
          <div style={{ textAlign: 'center', color: 'white', padding: '20px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <img
              src={imgSrc}
              alt={displayName}
              style={{ width: '240px', height: '240px', marginBottom: '20px', objectFit: 'contain' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '10px' }}>
              HQ Building Unlocked
            </div>
            <div style={{ fontSize: '1rem', color: '#ccc' }}>
              {displayName}
            </div>
          </div>
        );
      })()}
      
      {reward.type === 'duplicator' && (
        <div style={{ 
          textAlign: 'center', 
          color: 'white', 
          padding: '20px',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative'
        }}>
          <div style={{ 
            position: 'relative',
            width: '240px',
            height: '240px',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <img 
              src="/images/duplicator.jpg" 
              alt="Duplicator" 
              style={{ 
                width: '240px', 
                height: '240px', 
                objectFit: 'contain',
                position: 'relative'
              }}
              onError={(e) => {
                // Fallback to text if image fails to load
                e.target.style.display = 'none';
              }}
            />
            {/* Rarity line at the bottom of the image */}
            {reward.data?.rarity && (() => {
              const rarity = reward.data.rarity.toLowerCase();
              const rarityColors = {
                'legendary': '#FF8C00',  // Golden
                'epic': '#9B59B6',       // Purple
                'rare': '#4A90E2',       // Blue
                'common': '#808080'       // Gray
              };
              const lineColor = rarityColors[rarity] || '#808080';
              
              return (
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '240px',
                  height: '8px',
                  backgroundColor: lineColor,
                  borderRadius: '2px',
                  boxShadow: `0 0 10px ${lineColor}`
                }} />
              );
            })()}
          </div>
          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '10px' }}>
            {reward.data?.name || 'Order Duplicator'}
          </div>
          <div style={{ fontSize: '0.9rem', textTransform: 'capitalize', color: '#ccc' }}>
            {reward.data?.rarity || ''} Duplicator
          </div>
        </div>
      )}
      
      {reward.type === 'feature' && (() => {
        // Get image path based on featureKey and category
        const getFeatureImage = (category, featureKey) => {
          if (category === 'preparation_bonus') {
            if (featureKey === 'plan_ahead') return '/images/plan_ahead.jpg';
            if (featureKey === 'black_market') return '/images/black_market_dossier.jpg';
            if (featureKey === 'team_leader') return '/images/team_leader_briefing.jpg';
          } else if (category === 'diplomatic_accords') {
            if (featureKey === 'strategic_coordination') return '/images/strategic_coordination.jpg';
            if (featureKey === 'public_relations') return '/images/public_relations.jpg';
            if (featureKey === 'emergency_protocol') return '/images/emergency_protocol.jpg';
            if (featureKey === 'command_directive') return '/images/command_directive.jpg';
          }
          return null;
        };
        
        const imagePath = getFeatureImage(reward.data.category, reward.data.featureKey);
        
        return (
          <div style={{ textAlign: 'center', color: 'white', padding: '20px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            {imagePath && (
              <img 
                src={imagePath} 
                alt={reward.data.category === 'diplomatic_accords' ? 'Diplomatic Accord' : 'Preparation Bonus'} 
                style={{ width: '240px', height: '240px', marginBottom: '20px', objectFit: 'contain' }}
                onError={(e) => {
                  // Fallback to text if image fails to load
                  e.target.style.display = 'none';
                }}
              />
            )}
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '10px' }}>
              {reward.data.category === 'diplomatic_accords' ? 'Diplomatic Accord' : 'Preparation Bonus'}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#ccc' }}>
              {reward.data.featureKey}
            </div>
          </div>
        );
      })()}
      
      {reward.type === 'avatar' && (() => {
        // Extract avatar name from metadata or unlockKey
        let avatarName = null;
        if (reward.data?.metadata?.avatar_name) {
          avatarName = reward.data.metadata.avatar_name;
        } else if (reward.data?.unlockKey) {
          // Remove 'avatar_' prefix if present
          avatarName = reward.data.unlockKey.startsWith('avatar_') 
            ? reward.data.unlockKey.substring(7) 
            : reward.data.unlockKey;
        }
        const avatarImagePath = getAvatarImage(avatarName);
        
        return (
          <div style={{ textAlign: 'center', color: 'white', padding: '20px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <img 
              src={avatarImagePath} 
              alt={reward.data.name || 'Avatar'} 
              style={{ width: '240px', height: '240px', marginBottom: '20px', objectFit: 'contain' }}
              onError={(e) => {
                // Fallback to default avatar if image fails to load
                e.target.src = '/images/human_1.png';
              }}
            />
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '10px' }}>
              Avatar Unlocked
            </div>
            <div style={{ fontSize: '0.9rem', color: '#ccc' }}>
              {reward.data.name}
            </div>
          </div>
        );
      })()}
      
      {reward.type === 'emote' && (
        <div style={{ textAlign: 'center', color: 'white', padding: '20px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <img 
            src="/images/emotes.jpg" 
            alt="Emote" 
            style={{ width: '240px', height: '240px', marginBottom: '20px' }}
          />
          <div style={{ fontSize: '1.2rem', color: '#ccc' }}>
            {reward.data.speechBubble || reward.data.name}
          </div>
        </div>
      )}
      
    </div>
  );
}

export default DispatchAsset;
